<template>
  <div class="w-full flex flex-col">
    <div v-if="notification"
      :class="['fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg transition-all duration-300',
        notification.type === 'success' ? 'bg-green-500 text-white' :
        notification.type === 'error' ? 'bg-red-500 text-white' :
        notification.type === 'info' ? 'bg-blue-500 text-white' :
        'bg-gray-500 text-white']">
      <div class="flex items-center space-x-2">
        <span>{{ notification.message }}</span>
      </div>
    </div>
    <div class="pb-4 flex-shrink-0">
      <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-25 mb-1">
        {{ $t('AGENT_MGMT.SALESBOT.HEADER') }}
      </h2>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
        {{ $t('AGENT_MGMT.SALESBOT.HEADER_DESC') }}
      </p>
      <div class="border-b border-gray-200 dark:border-gray-700"></div>
    </div>
    <div>
      <div class="space-y-6 pb-6">
        <!-- Sidebar Navigation (always show) -->
        <div class="flex flex-row justify-stretch gap-2">
          <!-- Custom Tabs with SVG Icons -->
          <div class="flex flex-col gap-1 min-w-[200px] mr-4">
            <div
              v-for="tab in tabs"
              :key="tab.key"
              class="flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              :class="{
                'bg-woot-50 border-l-4 border-woot-500 text-woot-600 dark:bg-woot-900/50 dark:border-woot-400 dark:text-woot-400': tab.index === activeTabIndex,
                'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200': tab.index !== activeTabIndex,
              }"
              @click="activeTabIndex = tab.index"
            >
              <span
                :class="[
                  tab.icon,
                  'w-5 h-5 transition-all duration-200',
                  {
                    'text-woot-600 dark:text-woot-400': tab.index === activeTabIndex,
                    'text-gray-500 dark:text-gray-400': tab.index !== activeTabIndex,
                  }
                ]"
              />
              <span class="text-sm">{{ tab.name }}</span>
            </div>
          </div>
  
          <!-- Catalog Tab -->
          <div v-show="activeTabIndex === 0" class="w-full min-w-0">
            <div class="space-y-6">
              <!-- Loading State -->
              <div v-if="catalogLoading" class="flex flex-col items-center justify-center py-16 gap-4">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
                <p class="text-sm text-slate-500 dark:text-slate-400">{{ $t('AGENT_MGMT.COMMON.LOADING') }}</p>
              </div>
              
              <!-- Google Sheets Auth Flow -->
              <div v-else-if="catalogStep === 'auth'" class="gap-6">
                <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.CATALOG.SHEETS_TITLE') }}</label>
                <p class="text-gray-600 dark:text-gray-400">{{ $t('AGENT_MGMT.SALESBOT.CATALOG.SHEETS_AUTH_DESC') }}</p>
                <button
                  @click="connectGoogle"
                  class="inline-flex items-center space-x-3 bg-green-600 hover:bg-green-700 dark:bg-green-400 dark:hover:bg-green-500 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  :disabled="catalogLoading"
                >
                  <span>{{ $t('AGENT_MGMT.BOOKING_BOT.AUTH_BTN') }}</span>
                </button>
              </div>
              <div v-else-if="catalogStep === 'connected'" class="py-8">
                <div class="text-center mb-8">
                  <div class="w-16 h-16 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <h3 class="text-xl font-semibold text-slate-900 dark:text-slate-25 mb-2">{{ $t('AGENT_MGMT.BOOKING_BOT.CONNECTED_HEADER') }}</h3>
                  <p class="text-gray-600 dark:text-gray-400">{{ $t('AGENT_MGMT.BOOKING_BOT.CONNECTED_DESC') }}</p>
                  <p class="mt-2 text-sm text-gray-500">{{ catalogAccount?.email }}</p>
                  <div class="flex gap-4 center justify-center mt-4">
                    <template v-if="!salesAuthError">
                      <button
                        class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        @click="createSheets"
                        :disabled="catalogLoading"
                      >
                        <span v-if="catalogLoading">{{ $t('AGENT_MGMT.BOOKING_BOT.CREATE_SHEETS_LOADING') }}</span>
                        <span v-else>{{ $t('AGENT_MGMT.BOOKING_BOT.CREATE_SHEETS_BTN') }}</span>
                      </button>
                      
                      <button
                        class="inline-flex items-center space-x-2 border-2 border-red-600 hover:border-red-700 dark:border-red-400 dark:hover:border-red-500 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-500 px-4 py-2 rounded-md font-medium transition-colors bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20"
                        @click="disconnectGoogle"
                        :disabled="catalogLoading"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-ban">
                          <path d="M4.929 4.929 19.07 19.071"/>
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                        <span>{{ $t('AGENT_MGMT.BOOKING_BOT.DISC_BTN') }}</span>
                      </button>
                      </template>
                      <template v-else>
                        <div class="mt-3 text-red-600 text-sm flex items-center gap-2">
                          <p class="text-sm">{{ salesAuthError }}</p>
                          <button
                            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            @click="retryAuthentication"
                            :disabled="catalogLoading"
                          >
                            <span v-if="catalogLoading">{{ $t('AGENT_MGMT.BOOKING_BOT.RETRY_AUTH_LOADING') }}</span>
                            <span v-else>{{ $t('AGENT_MGMT.BOOKING_BOT.RETRY_AUTH_BTN') }}</span>
                          </button>
                        </div>
                      </template>
                  </div>
                  
                  <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    {{ $t('AGENT_MGMT.BOOKING_BOT.OR_RETRY_BTN') }}
                  </p>
                </div>
              </div>
              <div v-else-if="catalogStep === 'sheetConfig'">
                <!-- Input Sheet Section - Product Catalog -->
                <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 mb-6 border border-blue-200 dark:border-blue-800">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center mb-3">
                        <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                          <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 class="font-medium text-slate-900 dark:text-slate-25">
                            {{ $t('AGENT_MGMT.SALESBOT.CATALOG.INPUT_SHEET_TITLE') }}
                          </h3>
                          <p class="text-sm text-slate-600 dark:text-slate-400">
                            {{ $t('AGENT_MGMT.SALESBOT.CATALOG.INPUT_SHEET_DESC') }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-if="catalogSheets.input && !salesAuthError" class="flex flex-col gap-2">
                      <a 
                        :href="catalogSheets.input" 
                        target="_blank" 
                        class="inline-flex items-center space-x-2 border-2 border-green-600 hover:border-green-700 dark:border-green-600 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-500 px-4 py-2 rounded-md font-medium transition-colors bg-transparent hover:bg-green-50 dark:hover:bg-green-900/20"
                      >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                        {{ $t('AGENT_MGMT.BOOKING_BOT.OPEN_SHEET_BTN') }}
                      </a>
                    </div>
                  </div>
  
                  <div class="border-t border-blue-200 dark:border-blue-700 pt-6">
                    <div class="flex items-center justify-between">
                      <div>
                        <div v-if="!salesAuthError">
                          <button
                          @click="syncProductColumns"
                          :disabled="syncingColumns"
                          class="px-4 py-2 items-center bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <svg v-if="syncingColumns" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/>
                            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" class="opacity-75"/>
                          </svg>
                          <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                          </svg>
                          {{ syncingColumns ? $t('AGENT_MGMT.SALESBOT.CATALOG.SYNC_BUTTON_LOADING') : $t('AGENT_MGMT.SALESBOT.CATALOG.SYNC_BUTTON') }}
                        </button>
                        </div>
                      </div>
                      
                      <div class="flex flex-row"> 
                        <div class="text-red-600 text-sm flex items-center gap-2">
                          <button
                          @click="openRegenerateModal"
                          class="btn-retryauth inline-flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-colors bg-transparent"
                          :disabled="isRegenerating"
                        >
                          <span v-if="isRegenerating">{{ $t('AGENT_MGMT.BOOKING_BOT.RETRY_AUTH_LOADING') }}</span>
                          <span v-else>{{ t('AGENT_MGMT.BOOKING_BOT.RETRY_AUTH_BTN') }}</span>
                        </button>
                      </div>
                      <div class="gap-2 items-center">
                        <button
                          @click="disconnectGoogle"
                          class="inline-flex items-center space-x-2 border-2 border-red-600 hover:border-red-700 dark:border-red-400 dark:hover:border-red-500 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-500 px-4 py-2 rounded-md font-medium transition-colors bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 ml-3"
                          :disabled="loading"
                          >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-ban-icon lucide-ban"><path d="M4.929 4.929 19.07 19.071"/><circle cx="12" cy="12" r="10"/></svg>
                          <span>{{ $t('AGENT_MGMT.BOOKING_BOT.DISC_BTN') }}</span>
                        </button>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
  
                <!-- Output Sheet Section - Order Tracking -->
                <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 mb-6 border border-blue-200 dark:border-blue-800">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                        </svg>
                      </div>
                      <div>
                        <h4 class="font-medium text-slate-900 dark:text-slate-25">{{ $t('AGENT_MGMT.SALESBOT.CATALOG.OUTPUT_SHEET_TITLE') }}</h4>
                        <p class="text-sm text-slate-600 dark:text-slate-400">{{ $t('AGENT_MGMT.SALESBOT.CATALOG.OUTPUT_SHEET_DESC') }}</p>
                      </div>
                    </div>
                    <a 
                      v-if="catalogSheets.output && !salesAuthError"
                      :href="catalogSheets.output" 
                      target="_blank" 
                      class="inline-flex items-center space-x-2 border-2 border-green-600 hover:border-green-700 dark:border-green-600 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-500 px-4 py-2 rounded-md font-medium transition-colors bg-transparent hover:bg-green-50 dark:hover:bg-green-900/20"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                      </svg>
                      {{ $t('AGENT_MGMT.BOOKING_BOT.OPEN_SHEET_BTN') }}
                    </a>
                  </div>
                </div>

                <!-- Notification Settings -->
                <NotificationSettings
                  :ai-agent-id="data.id"
                  :categories="salesCategories"
                  :show-filters="true"
                  :multi-select-categories="true"
                  title-key="AGENT_MGMT.SALESBOT.NOTIFICATION.TITLE"
                  desc-key="AGENT_MGMT.SALESBOT.NOTIFICATION.DESC"
                  :variable-config="salesVariableConfig"
                />
              </div>
            </div>
          </div>

          <!-- Bot Config Tab -->
          <div v-show="activeTabIndex === 1" class="w-full min-w-0">
            <div class="flex flex-row gap-4">
              <div class="flex-1 min-w-0 flex flex-col justify-stretch">

                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 bg-white dark:bg-transparent">
                  <div class="flex items-center justify-between p-6">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-green-600 dark:text-green-400">
                          <path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>
                        </svg>
                      </div>
                      <div>
                        <h3 class="font-medium text-slate-900 dark:text-slate-25">{{ $t('AGENT_MGMT.DELAY.TITLE') }}</h3>
                        <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.DELAY.DESC') }}</p>
                      </div>
                    </div>

                    <label class="inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="isDelayEnabled" class="sr-only peer" :disabled="isSaving">
                      <div class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                      </div>
                    </label>
                  </div>
                  <div v-if="isDelayEnabled" class="border-t border-gray-200 dark:border-gray-700 p-6 space-y-4 transition-all duration-200 ease-in-out">
                    <div>
                      <label class="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-25">{{ $t('AGENT_MGMT.DELAY.INTERVAL') }}</label>
                      <div class="flex items-center gap-3">
                        <select
                          v-model="debounceInterval"
                          :disabled="isSaving"
                          class="text-center w-24 mb-0 p-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        >
                          <option class="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            v-for="opt in delayOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 bg-white dark:bg-transparent">
                  <div class="flex items-start justify-between p-6">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-green-600 dark:text-green-400">
                          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 class="font-medium text-slate-900 dark:text-slate-25">Tingkat Kreativitas</h3>
                        <p class="text-sm text-gray-500 mt-1">Tentukan seberapa kreatif bot dalam merespons percakapan</p>
                      </div>
                    </div>
                  </div>
                  
                  <div class="border-t border-gray-200 dark:border-gray-700 p-6">
                    <label class="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-25">Skala Kreativitas</label>
                    <div class="relative">
                      <select
                        v-model="creativityLevel"
                        :disabled="isSaving"
                        class="w-full mb-0 p-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option class="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100" v-for="opt in creativityOptions" :key="opt.value" :value="opt.value">
                          {{ opt.label }}
                        </option>
                      </select>
                      <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 dark:text-gray-400">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 bg-white dark:bg-transparent">
                  <div class="flex items-center justify-between p-6">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-green-600 dark:text-green-400">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </div>
                      <div>
                        <h3 class="font-medium text-slate-900 dark:text-slate-25">{{ $t('AGENT_MGMT.EOBOT.IDLE_STATE') }}</h3>
                        <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.EOBOT.IDLE_STATE_DESC') }}</p>
                      </div>
                    </div>
                    <label class="inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="idleConfig.enabled" class="sr-only peer" :disabled="isSaving">
                      <div class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>

                  <div v-if="idleConfig.enabled" class="border-t border-gray-200 dark:border-gray-700 p-6">
                    <div>
                      <label class="block text-sm font-medium mb-2 text-slate-900 dark:text-slate-25">
                        {{ $t('AGENT_MGMT.EOBOT.IDLE_TIME') }}
                      </label>
                      <select
                        v-model="idleConfig.duration"
                        :disabled="isSaving"
                        class="text-center w-24 mb-0 p-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                      >
                        <option :value="5">{{ $t('AGENT_MGMT.EOBOT.IDLE_TIME_OPTION_5_MIN') }}</option>
                        <option :value="10">{{ $t('AGENT_MGMT.EOBOT.IDLE_TIME_OPTION_10_MIN') }}</option>
                        <option :value="30">{{ $t('AGENT_MGMT.EOBOT.IDLE_TIME_OPTION_30_MIN') }}</option>
                        <option :value="60">{{ $t('AGENT_MGMT.EOBOT.IDLE_TIME_OPTION_1_HOUR') }}</option>
                        <option :value="120">{{ $t('AGENT_MGMT.EOBOT.IDLE_TIME_OPTION_2_HOURS') }}</option>
                        <option :value="1410">{{ $t('AGENT_MGMT.EOBOT.IDLE_TIME_OPTION_24_HOURS') }}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Reminder Otomatis Toggle -->
                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 bg-white dark:bg-transparent">
                  <div class="flex items-center p-6">
                    <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-green-600 dark:text-green-400">
                        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
                        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
                      </svg>
                    </div>
                    <div>
                      <h3 class="font-medium text-slate-900 dark:text-slate-25">{{ $t('AGENT_MGMT.REMINDER.APPOINTMENT.OFFER.TITLE') }}</h3>
                      <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.REMINDER.APPOINTMENT.OFFER.DESC.SALES') }}</p>
                    </div>
                  </div>
                  <div class="border-t border-gray-200 dark:border-gray-700 p-6">
                    <label class="inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="reminderOfferEnabled" :disabled="isSaving" class="sr-only peer">
                      <div
                        class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                      </div>
                      <span class="ml-3 text-sm text-slate-700 dark:text-slate-300">
                        {{ reminderOfferEnabled ? $t('AGENT_MGMT.REMINDER.ENABLED') : $t('AGENT_MGMT.REMINDER.DISABLED') }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div class="w-[240px] flex flex-col gap-3">
                <div class="sticky bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-10 h-10 flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-700 dark:text-slate-300">{{ $t('AGENT_MGMT.BOOKING_BOT.CONFIGURE') }}</h3>
                      <p class="text-sm text-slate-500 dark:text-slate-400">{{ $t('AGENT_MGMT.BOOKING_BOT.CONFIGURE_DESC') }}</p>
                    </div>
                  </div>
                  
                  <Button
                    class="w-full"
                    :is-loading="isSaving"
                    :disabled="isSaving"
                    @click="() => saveSettings()"
                  >
                    <span class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {{ $t('AGENT_MGMT.BOOKING_BOT.SAVE_BTN') }}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTabIndex === 2" class="w-full">
            <FileKnowledgeSources :data="data" context="sales"/>
          </div>

          <!-- Shipping Tab -->
          <div v-show="activeTabIndex === 3" class="w-full min-w-0">
            <ShippingConfig
              v-if="data && data.id"
              :ai-agent-id="data.id"
              :initial-stores="shippingStores" 
              :is-saving="isSaving"
              @save-config="submitShippingConfig"
            />
          </div>
  
          <!-- Shipping Tab
          <div v-show="activeTabIndex === 3" class="w-full min-w-0">
            <div class="flex flex-row gap-4">
              <div class="flex-1 min-w-0 flex flex-col justify-stretch gap-6">
                <div class="space-y-4">
                  <div>
                    <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.METHOD_TITLE') }}</label>
                
                
                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                  <div class="flex items-center justify-between p-4">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg">
                          <path class="fill-green-600 dark:fill-white"
                            d="M3 5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25v13.5a.75.75 0 0 1-.75.75h-1v-8.25c0-.11-.012-.219-.036-.325l-.739-3.326a3 3 0 0 0-2.928-2.349H8.453A3 3 0 0 0 5.525 7.6l-.74 3.325a1.5 1.5 0 0 0-.035.325v8.25h-1a.75.75 0 0 1-.75-.75V5.25Z"
                            fill="currentColor" />
                          <path class="fill-green-600 dark:fill-white"
                            d="M8.453 6a2.25 2.25 0 0 0-2.196 1.762l-.74 3.325a.75.75 0 0 0-.017.163v9c0 .966.784 1.75 1.75 1.75h1.5a1.75 1.75 0 0 0 1.75-1.75v-.75h3v.75c0 .966.784 1.75 1.75 1.75h1.5a1.75 1.75 0 0 0 1.75-1.75v-9a.748.748 0 0 0-.018-.163l-.739-3.325A2.25 2.25 0 0 0 15.547 6H8.453Zm-.732 2.087a.75.75 0 0 1 .732-.587h7.094a.75.75 0 0 1 .732.587l.536 2.413h-9.63l.536-2.413ZM7 20.25v-.75h2v.75a.25.25 0 0 1-.25.25h-1.5a.25.25 0 0 1-.25-.25Zm10-.75v.75a.25.25 0 0 1-.25.25h-1.5a.25.25 0 0 1-.25-.25v-.75h2Zm-6.25-3h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1 0-1.5Zm-.745-2.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM15 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
                            fill="currentColor" />
                        </svg>
                      </div>
                      <div>
                        <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.STORE_COURIER') }}</h3>
                        <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.DESC_STORE_COURIER') }}</p>
                      </div>
                    </div>
  
                    <label class="inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="shippingMethods.kurirToko" class="sr-only peer">
                      <div
                        class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                      </div>
                    </label>
                  </div>
                  
                  <div 
                    v-if="shippingMethods.kurirToko" 
                    class="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 transition-all duration-200 ease-in-out"
                  >
                    
                    <div>
                      <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.STORE_ADDRESS') }}</label>
                      <input 
                        type="text" 
                        class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                        :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.STORE_ADDRESS_PLACEHOLDER')" 
                        v-model="kurirToko.alamat" 
                      />
                    </div>
  
                    
                    <div>
                      <label class="block font-medium mb-2">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.MAP_LOCATION') }}</label>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.MAP_INSTRUCTION') }}</p>
                      
                      
                      <div class="relative bg-gray-100 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                        <div 
                          ref="mapRef"
                          class="w-full h-64"
                          style="min-height: 256px;"
                        ></div>
                        
                        
                        <div v-if="!kurirToko.mapLoaded" class="absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <div class="text-center">
                            <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/>
                              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" class="opacity-75"/>
                            </svg>
                            <p class="text-sm text-gray-600 dark:text-gray-400">Loading map...</p>
                          </div>
                        </div>
                      </div>
  
                      
                      <div class="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <label class="block text-xs font-medium text-gray-500 mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.LATITUDE') }}</label>
                          <input 
                            type="number" 
                            step="0.000001"
                            v-model="kurirToko.latitude"
                            class="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-700 cursor-not-allowed"
                            readonly
                          />
                        </div>
                        <div>
                          <label class="block text-xs font-medium text-gray-500 mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.LONGITUDE') }}</label>
                          <input 
                            type="number" 
                            step="0.000001"
                            v-model="kurirToko.longitude"
                            class="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-700 cursor-not-allowed"
                            readonly
                          />
                        </div>
                      </div>
                    </div>
                    
                    
                    <div>
                      <label class="block font-medium mb-3">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SERVICE_AREA') }}</label>
                      <div class="space-y-4">
                        
                        <div class="flex items-start space-x-3">
                          <label class="inline-flex items-center cursor-pointer">
                            <input 
                              type="radio" 
                              v-model="kurirToko.serviceAreaType" 
                              value="radius"
                              class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 focus:ring-2"
                            />
                          </label>
                          <div class="flex-1">
                            <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SERVICE_RADIUS') }}</label>
                            <div class="relative">
                              <input 
                                type="number" 
                                min="0"
                                step="0.1"
                                :disabled="kurirToko.serviceAreaType !== 'radius'"
                                class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !pr-10 !pl-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                                :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.SERVICE_RADIUS_PLACEHOLDER')" 
                                v-model="kurirToko.radius" 
                              />
                              <span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">km</span>
                            </div>
                          </div>
                        </div>
  
                        
                        <div class="flex items-start space-x-3">
                          <label class="inline-flex items-center cursor-pointer">
                            <input 
                              type="radio" 
                              v-model="kurirToko.serviceAreaType" 
                              value="region"
                              class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 focus:ring-2"
                            />
                          </label>
                          <div class="flex-1">
                            <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SERVICE_REGION') }}</label>
                            <div class="dropdown-menu dropdown-container bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60" ref="serviceAreaDropdownRef">
                              <div class="relative">
                                <input
                                  v-model="serviceAreaProvinsiSearchQuery"
                                  type="text"
                                  :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.PROVINCE_LABEL_PLACEHOLDER')"
                                  :disabled="kurirToko.serviceAreaType !== 'region'"
                                  class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                                  @input="onServiceAreaProvinsiSearch"
                                  @click="toggleServiceAreaProvinsiDropdown"
                                  :readonly="loadingProvinsi || kurirToko.serviceAreaType !== 'region'"
                                  :value="selectedServiceAreaProvinsiName"
                                />
                                <button
                                  type="button"
                                  @click="toggleServiceAreaProvinsiDropdown"
                                  :disabled="loadingProvinsi || kurirToko.serviceAreaType !== 'region'"
                                  class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                  </svg>
                                </button>
                              </div>
                              
                              <div
                                v-if="isServiceAreaProvinsiDropdownOpen && kurirToko.serviceAreaType === 'region'"
                                class="dropdown-menu absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto z-50"
                              >
                                <div
                                  v-for="provinsi in filteredServiceAreaProvinsiOptions"
                                  :key="provinsi.id"
                                  @click="selectServiceAreaProvinsi(provinsi)"
                                  class="dropdown-item px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-gray-100 text-sm"
                                >
                                  {{ provinsi.name }}
                                </div>
                                <div v-if="filteredServiceAreaProvinsiOptions.length === 0" class="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                  {{ $t('AGENT_MGMT.SALESBOT.SHIPPING.NO_PROVINCE_FOUND') }}
                                </div>
                              </div>
                            </div>
                            
                            
                            <div v-if="kurirToko.serviceAreaType === 'region' && kurirToko.wilayah" class="mt-3">
                              <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.CITY_LABEL') }}</label>
                              <div class="dropdown-container" ref="serviceAreaKotaDropdownRef">
                                <div class="relative">
                                  <input
                                    v-model="serviceAreaKotaSearchQuery"
                                    type="text"
                                    :placeholder="selectedServiceAreaKotaName || (loadingServiceAreaKota ? 'Loading...' : $t('AGENT_MGMT.SALESBOT.SHIPPING.CITY_LABEL_PLACEHOLDER'))"
                                    :disabled="!kurirToko.wilayah || loadingServiceAreaKota"
                                    class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                                    @input="onServiceAreaKotaSearch"
                                    @click="toggleServiceAreaKotaDropdown"
                                    :readonly="!kurirToko.wilayah || loadingServiceAreaKota"
                                    :value="selectedServiceAreaKotaName"
                                  />
                                  <button
                                    type="button"
                                    @click="toggleServiceAreaKotaDropdown"
                                    :disabled="!kurirToko.wilayah || loadingServiceAreaKota"
                                    class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                  </button>
                                </div>
                                
                                <div
                                  v-if="isServiceAreaKotaDropdownOpen && kurirToko.wilayah"
                                  class="dropdown-menu absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto z-50"
                                >
                                  <div v-if="loadingServiceAreaKota" class="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                    Loading cities...
                                  </div>
                                  <div
                                    v-for="kota in filteredServiceAreaKotaOptions"
                                    :key="kota.id"
                                    @click="selectServiceAreaKota(kota)"
                                    class="dropdown-item px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-gray-100 text-sm"
                                  >
                                    {{ kota.name }}
                                  </div>
                                  <div v-if="!loadingServiceAreaKota && filteredServiceAreaKotaOptions.length === 0 && kurirToko.wilayah" class="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                    {{ $t('AGENT_MGMT.SALESBOT.SHIPPING.NO_CITY_FOUND') }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
  
                    
                    <div>
                      <label class="block font-medium mb-3">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SHIPPING_COST') }}</label>
                      <div class="space-y-4">
                        
                        <div class="space-y-3">
                          
                          <div class="flex items-start space-x-3">
                            <label class="inline-flex items-center cursor-pointer">
                              <input 
                                type="radio" 
                                v-model="kurirToko.pricingMethod" 
                                value="flatRate"
                                class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 focus:ring-2"
                              />
                            </label>
                            <div class="flex-1">
                              <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.FLAT_RATE') }}</label>
                              <div class="relative">
                                <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">Rp</span>
                                <input 
                                  type="number" 
                                  min="0"
                                  :disabled="kurirToko.pricingMethod !== 'flatRate'"
                                  class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !pl-8 !pr-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                                  :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.FLAT_RATE_PLACEHOLDER')" 
                                  v-model="kurirToko.flatRate" 
                                />
                              </div>
                            </div>
                          </div>
  
                          
                          <div class="flex items-start space-x-3">
                            <label class="inline-flex items-center cursor-pointer">
                              <input 
                                type="radio" 
                                v-model="kurirToko.pricingMethod" 
                                value="perDistance"
                                class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 focus:ring-2"
                              />
                            </label>
                            <div class="flex-1">
                              <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.COST_PER_DISTANCE') }}</label>
                              <input 
                                type="number" 
                                min="0"
                                :disabled="kurirToko.pricingMethod !== 'perDistance'"
                                class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                                :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.COST_PER_DISTANCE_PLACEHOLDER')" 
                                v-model="kurirToko.biayaPerJarak" 
                              />
                            </div>
                          </div>
                        </div>
  
                        
                        <div class="flex items-start space-x-3">
                          <label class="inline-flex items-center cursor-pointer">
                            <input type="checkbox" v-model="kurirToko.gratisOngkir" class="sr-only peer">
                            <div
                              class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                            </div>
                          </label>
                          <div class="flex-1">
                            <label class="block text-sm font-medium">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.FREE_SHIPPING') }}</label>
                            <p class="text-xs text-gray-500 mt-1">Aktifkan gratis ongkir dengan syarat minimal belanja</p>
                          </div>
                        </div>
  
                        
                        <div v-if="kurirToko.gratisOngkir" class="ml-14 transition-all duration-200 ease-in-out">
                          <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.MIN_PURCHASE') }}</label>
                          <div class="relative">
                            <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">Rp</span>
                            <input 
                              type="number" 
                              min="0"
                              class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !pl-8 !pr-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                              :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.MIN_PURCHASE_PLACEHOLDER')" 
                              v-model="kurirToko.minimalBelanja" 
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                     <div>
                        <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.DELIVERY_TIME') }}</label>
                        <input 
                          type="text" 
                          class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                          :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.PICKUP_TIME_PLACEHOLDER')" 
                          v-model="kurirToko.estimasi" 
                        />
                      </div>
                  </div>
                </div>
  
                
                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                  <div class="flex items-center justify-between p-4">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-white lucide lucide-package-open-icon lucide-package-open"><path d="M12 22v-9"/><path d="M15.17 2.21a1.67 1.67 0 0 1 1.63 0L21 4.57a1.93 1.93 0 0 1 0 3.36L8.82 14.79a1.655 1.655 0 0 1-1.64 0L3 12.43a1.93 1.93 0 0 1 0-3.36z"/><path d="M20 13v3.87a2.06 2.06 0 0 1-1.11 1.83l-6 3.08a1.93 1.93 0 0 1-1.78 0l-6-3.08A2.06 2.06 0 0 1 4 16.87V13"/><path d="M21 12.43a1.93 1.93 0 0 0 0-3.36L8.83 2.2a1.64 1.64 0 0 0-1.63 0L3 4.57a1.93 1.93 0 0 0 0 3.36l12.18 6.86a1.636 1.636 0 0 0 1.63 0z"/></svg>
                      </div>
                      <div>
                        <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.REGULAR_COURIER') }}</h3>
                        <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.DESC_REGULAR_COURIER') }}</p>
                      </div>
                    </div>
                    <label class="inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="shippingMethods.kurirBiasa" class="sr-only peer">
                      <div
                        class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                      </div>
                    </label>
                  </div>
                  
                  <div 
                    v-if="shippingMethods.kurirBiasa" 
                    class="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 transition-all duration-200 ease-in-out"
                  >
                  
                  <div class="flex items-center justify-center py-8">
                    <div class="text-center">
                      <div class="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12,6 12,12 16,14"/>
                        </svg>
                      </div>
                      <h4 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Coming Soon</h4>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Regular courier configuration will be available soon.</p>
                    </div>
                  </div> -->

                  <!-- DONT DELETE! -->
                    <!-- <div>
                      <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.ORIGIN_ADDRESS') }}</label>
                      
                      <div class="mb-3">
                        <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.PROVINCE_LABEL') }}</label>
                        <div class="relative dropdown-container" ref="provinsiDropdownRef">
                          <div
                            class="dropdown-input w-full mb-0 p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer flex items-center justify-between"
                            :class="{ 'disabled:bg-gray-100 disabled:cursor-not-allowed': loadingProvinsi }"
                            @click="toggleProvinsiDropdown"
                          >
                            <input
                              v-model="provinsiSearchQuery"
                              :placeholder="selectedProvinsiName || (loadingProvinsi ? 'Loading...' : $t('AGENT_MGMT.SALESBOT.SHIPPING.PROVINCE_LABEL_PLACEHOLDER'))"
                              class="flex-1 bg-transparent outline-none"
                              :disabled="loadingProvinsi"
                              @input="onProvinsiSearch"
                              @click.stop
                              @focus="isProvinsiDropdownOpen = true"
                            />
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          </div>
                          <div
                            v-show="isProvinsiDropdownOpen"
                            class="dropdown-menu absolute z-[9999] w-full top-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden"
                          >
                            <div class="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                              <div v-if="loadingProvinsi" class="px-3 py-2 text-sm text-gray-500">
                                Loading provinces...
                              </div>
                              <div
                                v-for="provinsi in filteredProvinsiOptions"
                                :key="provinsi.id"
                                class="dropdown-item px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                                @click="selectProvinsi(provinsi)"
                              >
                                {{ provinsi.name }}
                              </div>
                              <div v-if="!loadingProvinsi && filteredProvinsiOptions.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                No provinces found
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
  
                      <div class="mb-3">
                        <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.CITY_LABEL') }}</label>
                        <div class="relative dropdown-container" ref="kotaDropdownRef">
                          <div
                            class="dropdown-input w-full mb-0 p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer flex items-center justify-between"
                            :class="{ 'disabled:bg-gray-100 disabled:cursor-not-allowed': !kurirBiasa.provinsi || loadingKota }"
                            @click="toggleKotaDropdown"
                          >
                            <input
                              v-model="kotaSearchQuery"
                              :placeholder="selectedKotaName || (!kurirBiasa.provinsi ? $t('AGENT_MGMT.SALESBOT.SHIPPING.CITY_SELECT_FIRST') : loadingKota ? 'Loading...' : $t('AGENT_MGMT.SALESBOT.SHIPPING.CITY_LABEL_PLACEHOLDER'))"
                              class="flex-1 bg-transparent outline-none"
                              :disabled="!kurirBiasa.provinsi || loadingKota"
                              @input="onKotaSearch"
                              @click.stop
                              @focus="isKotaDropdownOpen = true"
                            />
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          </div>
                          <div
                            v-show="isKotaDropdownOpen"
                            class="dropdown-menu absolute z-[9999] w-full top-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden"
                          >
                            <div class="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                              <div v-if="loadingKota" class="px-3 py-2 text-sm text-gray-500">
                                Loading cities...
                              </div>
                              <div
                                v-for="kota in filteredKotaOptions"
                                :key="kota.id"
                                class="dropdown-item px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                                @click="selectKota(kota)"
                              >
                                {{ kota.name }}
                              </div>
                              <div v-if="!loadingKota && filteredKotaOptions.length === 0 && kurirBiasa.provinsi" class="px-3 py-2 text-sm text-gray-500">
                                No cities found
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
  
                      <div class="mb-3">
                        <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SUBDISTRICT_LABEL') }}</label>
                        <div class="relative dropdown-container" ref="kecamatanDropdownRef">
                          <div
                            class="dropdown-input w-full mb-0 p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer flex items-center justify-between"
                            :class="{ 'disabled:bg-gray-100 disabled:cursor-not-allowed': !kurirBiasa.kota || loadingKecamatan }"
                            @click="toggleKecamatanDropdown"
                          >
                            <input
                              v-model="kecamatanSearchQuery"
                              :placeholder="selectedKecamatanName || (!kurirBiasa.kota ? $t('AGENT_MGMT.SALESBOT.SHIPPING.SUBDISTRICT_SELECT_FIRST') : loadingKecamatan ? 'Loading...' : $t('AGENT_MGMT.SALESBOT.SHIPPING.SUBDISTRICT_LABEL_PLACEHOLDER'))"
                              class="flex-1 bg-transparent outline-none"
                              :disabled="!kurirBiasa.kota || loadingKecamatan"
                              @input="onKecamatanSearch"
                              @click.stop
                              @focus="isKecamatanDropdownOpen = true"
                            />
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          </div>
                          <div
                            v-show="isKecamatanDropdownOpen"
                            class="dropdown-menu absolute z-[9999] w-full top-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden"
                          >
                            <div class="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                              <div v-if="loadingKecamatan" class="px-3 py-2 text-sm text-gray-500">
                                Loading subdistricts...
                              </div>
                              <div
                                v-for="kecamatan in filteredKecamatanOptions"
                                :key="kecamatan.id"
                                class="dropdown-item px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                                @click="selectKecamatan(kecamatan)"
                              >
                                {{ kecamatan.name }}
                              </div>
                              <div v-if="!loadingKecamatan && filteredKecamatanOptions.length === 0 && kurirBiasa.kota" class="px-3 py-2 text-sm text-gray-500">
                                No subdistricts found
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
  
                      <div class="mb-3">
                        <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.WARD_LABEL') }}</label>
                        <div class="relative dropdown-container" ref="kelurahanDropdownRef">
                          <div
                            class="dropdown-input w-full mb-0 p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer flex items-center justify-between"
                            :class="{ 'disabled:bg-gray-100 disabled:cursor-not-allowed': !kurirBiasa.kecamatan || loadingKelurahan }"
                            @click="toggleKelurahanDropdown"
                          >
                            <input
                              v-model="kelurahanSearchQuery"
                              :placeholder="selectedKelurahanName || (!kurirBiasa.kecamatan ? $t('AGENT_MGMT.SALESBOT.SHIPPING.WARD_SELECT_FIRST') : loadingKelurahan ? 'Loading...' : $t('AGENT_MGMT.SALESBOT.SHIPPING.WARD_LABEL_PLACEHOLDER'))"
                              class="flex-1 bg-transparent outline-none"
                              :disabled="!kurirBiasa.kecamatan || loadingKelurahan"
                              @input="onKelurahanSearch"
                              @click.stop
                              @focus="isKelurahanDropdownOpen = true"
                            />
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          </div>
                          <div
                            v-show="isKelurahanDropdownOpen"
                            class="dropdown-menu absolute z-[9999] w-full top-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden"
                          >
                            <div class="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                              <div v-if="loadingKelurahan" class="px-3 py-2 text-sm text-gray-500">
                                Loading villages...
                              </div>
                              <div
                                v-for="kelurahan in filteredKelurahanOptions"
                                :key="kelurahan.id"
                                class="dropdown-item px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                                @click="selectKelurahan(kelurahan)"
                              >
                                {{ kelurahan.name }}
                              </div>
                              <div v-if="!loadingKelurahan && filteredKelurahanOptions.length === 0 && kurirBiasa.kecamatan" class="px-3 py-2 text-sm text-gray-500">
                                No villages found
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
  
                      <div class="mb-3">
                        <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.STREET_LABEL') }}</label>
                        <input 
                          type="text" 
                          v-model="kurirBiasa.jalan"
                          :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.STREET_LABEL_PLACEHOLDER')" 
                          class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                        />
                      </div>
  
                      <div class="mb-3">
                        <label class="block text-sm font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.ZIP_CODE_LABEL') }}</label>
                        <input 
                          type="text" 
                          v-model="kurirBiasa.kodePos"
                          :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.ZIP_CODE_LABEL_PLACEHOLDER')"
                          maxlength="5"
                          pattern="[0-9]{5}"
                          class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                        />
                      </div>
                    </div>
                    <div>
                      <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SELECT_COURIER') }}</label>
                      <div class="relative" ref="kurirDropdownRef">
                        <div
                          @click="isKurirDropdownOpen = !isKurirDropdownOpen"
                          class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-auto !px-3 !py-2.5 !mb-4 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer flex items-center justify-between min-h-[40px]"
                        >
                          <div class="flex-1 text-left">
                            <span v-if="kurirBiasa.kurir.length === 0" class="text-gray-500">
                              {{ $t('AGENT_MGMT.SALESBOT.SHIPPING.SELECT_COURIER_PLACEHOLDER') }}
                            </span>
                            <div v-else class="flex flex-wrap gap-1">
                              <span
                                v-for="kurirId in kurirBiasa.kurir"
                                :key="kurirId"
                                class="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                              >
                                {{ kurirOptions.find(t => t.id === kurirId)?.label }}
                                <button
                                  type="button"
                                  @click.stop="toggleKurir(kurirId)"
                                  class="ml-1 hover:text-green-600"
                                >
                                  <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                                    />
                                  </svg>
                                </button>
                              </span>
                            </div>
                          </div>
                          <svg
                            class="w-5 h-5 text-gray-400 transition-transform"
                            :class="{ 'rotate-180': isKurirDropdownOpen }"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        <div
                          v-show="isKurirDropdownOpen"
                          class="absolute z-10 w-full top-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto"
                        >
                          <div
                            v-for="kurir in kurirOptions"
                            :key="kurir.id"
                            @click="toggleKurir(kurir.id)"
                            class="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-900 dark:text-gray-100 transition-colors duration-150"
                            :class="{ 
                              'bg-blue-50 dark:bg-blue-900/50 text-blue-900 dark:text-blue-100': isKurirSelected(kurir.id),
                              'hover:bg-gray-50 dark:hover:bg-slate-700': !isKurirSelected(kurir.id)
                            }"
                          >
                            <input
                              type="checkbox"
                              :checked="isKurirSelected(kurir.id)"
                              class="mr-3 text-blue-600 dark:text-blue-400 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
                              @click.stop
                            >
                            <span class="flex-1 text-sm">{{ kurir.label }}</span>
                            <svg
                              v-if="isKurirSelected(kurir.id)"
                              class="w-4 h-4 text-blue-600 dark:text-blue-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div v-if="kurirBiasa.kurir.length > 0 && kurirBiasa.kota" class="mb-4">
                        <button
                          @click="calculateShippingCosts"
                          :disabled="loadingShippingCost"
                          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <svg v-if="loadingShippingCost" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/>
                            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" class="opacity-75"/>
                          </svg>
                          <span>{{ loadingShippingCost ? 'Calculating...' : 'Calculate Shipping Cost' }}</span>
                        </button>
                      </div>
  
                      <div v-if="Object.keys(kurirBiasa.shippingCosts).length > 0" class="mb-4">
                        <h4 class="font-medium mb-2">Shipping Cost Estimates:</h4>
                        <div class="space-y-2">
                          <div 
                            v-for="(costs, courier) in kurirBiasa.shippingCosts" 
                            :key="courier"
                            class="border rounded-lg p-3 bg-gray-50"
                          >
                            <h5 class="font-medium text-sm mb-1">{{ courier.toUpperCase() }}</h5>
                            <div v-if="costs.length > 0" class="space-y-1">
                              <div 
                                v-for="service in costs" 
                                :key="service.service"
                                class="flex justify-between text-xs"
                              >
                                <span>{{ service.service }} ({{ service.description }})</span>
                                <span class="font-medium">Rp {{ service.cost[0].value.toLocaleString() }}</span>
                              </div>
                            </div>
                            <div v-else class="text-xs text-gray-500">No services available</div>
                          </div>
                        </div>
                      </div>
                    </div> 
                  </div>
                </div> -->
  
                <!-- Ambil ke Toko
                <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                  <div class="flex items-center justify-between p-4">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                        <svg class="fill-green-600 dark:fill-white" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M10.495 14.501v7.498H7.498v-7.498h2.996Zm6.76-1.5h-3.502a.75.75 0 0 0-.75.75v3.502c0 .414.336.75.75.75h3.502a.75.75 0 0 0 .75-.75v-3.502a.75.75 0 0 0-.75-.75Zm-.751 1.5v2.002h-2.001v-2.002h2ZM8.166 7.002H3.5v1.165c0 1.18.878 2.157 2.016 2.311l.157.016.16.005c1.234 0 2.245-.959 2.327-2.173l.005-.16V7.003Zm6.165 0H9.666v1.165c0 1.18.878 2.157 2.016 2.311l.157.016.16.005c1.234 0 2.245-.959 2.327-2.173l.005-.16V7.003Zm6.167 0h-4.665v1.165c0 1.18.878 2.157 2.017 2.311l.156.016.16.005c1.235 0 2.245-.959 2.327-2.173l.006-.16-.001-1.164ZM9.06 3.5H6.326L4.469 5.502h3.977L9.06 3.5Zm4.307 0H10.63l-.616 2.002h3.97L13.369 3.5Zm4.305 0h-2.734l.614 2.002h3.977L17.673 3.5ZM2.2 5.742l3.25-3.502a.75.75 0 0 1 .446-.233L6 2h12a.75.75 0 0 1 .474.169l.076.07 3.272 3.53.03.038c.102.136.148.29.148.44L22 8.168c0 .994-.379 1.9-1 2.58V21.25a.75.75 0 0 1-.649.743L20.25 22l-8.254-.001v-8.248a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v8.248L3.75 22a.75.75 0 0 1-.743-.648l-.007-.102V10.748a3.818 3.818 0 0 1-.995-2.384l-.005-.197V6.29a.728.728 0 0 1 .096-.408l.05-.076.054-.065Z"/></svg>                    </div>
                      <div>
                        <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.PICKUP_STORE') }}</h3>
                        <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.DESC_PICKUP_STORE') }}</p>
                      </div>
                    </div>
                    <label class="inline-flex items-center cursor-pointer">
                      <input type="checkbox" v-model="shippingMethods.ambilToko" class="sr-only peer">
                      <div
                        class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                      </div>
                    </label>
                  </div>
                  
                  <div 
                    v-if="shippingMethods.ambilToko" 
                    class="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 transition-all duration-200 ease-in-out"
                  >
                    <div>
                      <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.BRANCH_ADDRESS') }}</label>
                      <input 
                        type="text" 
                        class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                        :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.BRANCH_ADDRESS_PLACEHOLDER')" 
                        v-model="ambilToko.alamat" 
                      />
                    </div>
                    <div>
                      <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.OPERATION_HOURS') }}</label>
                      <div class="flex gap-2 items-center">
                        <div class="flex-1">
                          <label class="block text-sm text-gray-600 mb-1">Jam Buka</label>
                          <input 
                            type="time" 
                            v-model="ambilToko.jamBuka"
                            class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                          />
                        </div>
                        <div class="pt-6 text-gray-500">-</div>
                        <div class="flex-1">
                          <label class="block text-sm text-gray-600 mb-1">Jam Tutup</label>
                          <input 
                            type="time" 
                            v-model="ambilToko.jamTutup"
                            class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.PICKUP_TIME') }}</label>
                      <input 
                        type="text" 
                        class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out" 
                        :placeholder="$t('AGENT_MGMT.SALESBOT.SHIPPING.PICKUP_TIME_PLACEHOLDER')" 
                        v-model="ambilToko.estimasi" 
                      />
                    </div>
                  </div>
                </div>
                  </div>
                </div>
              </div>
              
              <div class="w-[240px] flex flex-col gap-3">
                <div class="sticky top-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2b9966" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-truck-icon lucide-truck"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-700 dark:text-slate-300">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.HEADER') }}</h3>
                      <p class="text-sm text-slate-500 dark:text-slate-400">{{ $t('AGENT_MGMT.SALESBOT.SHIPPING.HEADER_DESC') }}</p>
                    </div>
                  </div>
                  
                  <Button
                    class="w-full"
                    :is-loading="isSaving"
                    :disabled="isSaving"
                    @click="() => submitShippingConfig()"
                  >
                    <span class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {{ $t('AGENT_MGMT.FORM_CREATE.SUBMIT') }}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div> -->
  
          <!-- Payment Methods Tab -->
          <div v-show="activeTabIndex === 4" class="w-full min-w-0">
            <div class="flex flex-row gap-4">
              <div class="flex-1 min-w-0 flex flex-col justify-stretch gap-6">
                <div class="space-y-4">
                  <div>
                    <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.HEADER') }}</label>
  
                    <!-- COD -->
                    <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                      <div class="flex items-center justify-between p-4">
                        <div class="flex items-center">
                          <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-white lucide lucide-circle-dollar-sign-icon lucide-circle-dollar-sign"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/></svg> 
                          </div>
                          <div>
                            <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.COD_TITLE') }}</h3>
                            <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.COD_DESC') }}</p>
                          </div>
                        </div>
                        <label class="inline-flex items-center cursor-pointer">
                          <input type="checkbox" v-model="paymentMethods.cod" :disabled="isSaving" class="sr-only peer">
                          <div
                            class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                          </div>
                        </label>
                      </div>
                    </div>
  
                    <!-- Bank Transfer -->
                    <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                      <div class="flex items-center justify-between p-4">
                        <div class="flex items-center">
                          <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-white lucide lucide-landmark-icon lucide-landmark"><path d="M10 18v-7"/><path d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z"/><path d="M14 18v-7"/><path d="M18 18v-7"/><path d="M3 22h18"/><path d="M6 18v-7"/></svg>
                          </div>
                          <div>
                            <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.BANK_TRANSFER_TITLE') }}</h3>
                            <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.BANK_TRANSFER_DESC') }}</p>
                          </div>
                        </div>
                        <label class="inline-flex items-center cursor-pointer">
                          <input type="checkbox" v-model="paymentMethods.bankTransfer" :disabled="isSaving" class="sr-only peer">
                          <div
                            class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                          </div>
                        </label>
                      </div>
                      
                      <div 
                        v-if="paymentMethods.bankTransfer" 
                        class="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 transition-all duration-200 ease-in-out"
                      >
                        <div class="space-y-4">
                          <div
                            v-for="(account, index) in bankAccounts"
                            :key="account.id"
                            class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
                          >
                            <div class="flex items-center justify-between mb-4">
                              <h5 class="font-medium text-slate-700 dark:text-slate-300">
                                {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.BANK_ACCOUNT_TITLE')}} #{{ index + 1 }}
                              </h5>
                              <Button
                                variant="ghost"
                                color="ruby"
                                icon="i-lucide-trash"
                                size="sm"
                                :disabled="isSaving"
                                @click="() => deleteBankAccount(index)"
                                class="opacity-70 hover:opacity-100"
                              />
                            </div>
                            
                            <div class="grid grid-cols-1 gap-4">
                              <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.BANK_NAME_LABEL') }} <span class="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  v-model="account.bankName"
                                  :disabled="isSaving"
                                  placeholder="e.g., Bank BCA, Bank Mandiri"
                                  class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                                />
                              </div>
                              <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.ACCOUNT_NUMBER_LABEL') }} <span class="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  v-model="account.accountNumber"
                                  :disabled="isSaving"
                                  placeholder="e.g., 1234567890"
                                  class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                                />
                              </div>
                              <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.ACCOUNT_HOLDER_LABEL') }} <span class="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  v-model="account.accountHolder"
                                  :disabled="isSaving"
                                  placeholder="e.g., John Doe"
                                  class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
  
                        <Button
                          class="w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-green-400 hover:text-green-600 transition-all duration-200 rounded-xl bg-transparent hover:bg-green-50 dark:hover:bg-green-900/10"
                          variant="ghost"
                          :disabled="isSaving"
                          @click="addBankAccount"
                        >
                          <span class="flex items-center gap-2">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.ADD_BANK_ACCOUNT') }}
                          </span>
                        </Button>
                      </div>
                    </div>
  
                    <!-- QRIS Payment -->
                    <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                      <div class="flex items-center justify-between p-4">
                        <div class="flex items-center">
                          <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-white lucide lucide-qr-code"><rect width="8" height="8" x="3" y="3" rx="1"/><rect width="8" height="8" x="13" y="3" rx="1"/><rect width="8" height="8" x="3" y="13" rx="1"/><circle cx="17.5" cy="17.5" r="1.5"/></svg>
                          </div>
                          <div>
                            <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS_TITLE') }}</h3>
                            <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS_DESC') }}</p>
                          </div>
                        </div>
                        <label class="inline-flex items-center cursor-pointer">
                          <input type="checkbox" v-model="paymentMethods.qris" :disabled="isSaving" class="sr-only peer">
                          <div
                            class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                          </div>
                        </label>
                      </div>
  
                      <!-- QRIS Configuration Section -->
                      <div v-if="paymentMethods.qris" class="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 transition-all duration-200 ease-in-out">
    
                        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                          <div class="flex items-center justify-between mb-4">
                            <h5 class="font-medium text-slate-700 dark:text-slate-300">
                              QRIS Code
                            </h5>
                            <Button
                              v-if="qrisConfig.imageUrl"
                              variant="ghost"
                              color="ruby"
                              icon="i-lucide-trash"
                              size="sm"
                              :disabled="isSaving"
                              @click="deleteQrisImage"
                              class="opacity-70 hover:opacity-100"
                            />
                          </div>

                          <!-- QRIS Image Upload -->
                          <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS_IMAGE_LABEL') }} <span class="text-red-500">*</span>
                            </label>
                            <div class="flex flex-col gap-3">
                              <input
                                type="file"
                                ref="qrisImageInput"
                                accept="image/*"
                                :disabled="isSaving"
                                @change="handleQrisImageUpload"
                                class="hidden"
                              />
                              <button
                                @click="$refs.qrisImageInput?.click()"
                                :disabled="isSaving"
                                class="px-4 py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 hover:border-green-400 hover:text-green-600 transition-all bg-white dark:bg-slate-800"
                              >
                                {{ qrisConfig.imageUrl ? $t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS_CHANGE_IMAGE') : $t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS_UPLOAD_IMAGE') }}
                              </button>
                              <div v-if="qrisConfig.imageUrl" class="flex-1 text-sm text-gray-600 dark:text-gray-400">
                                <div class="flex flex-col items-center">
                                  <p class="mb-3 text-center">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS_IMAGE_PREVIEW') }}</p>
                                  <img :src="qrisConfig.imageUrl" class="w-48 h-48 object-contain border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-slate-800" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
  
                    <!-- Payment Gateway -->
                    <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                      <div class="flex items-center justify-between p-4">
                        <div class="flex items-center">
                          <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-white lucide lucide-credit-card-icon lucide-credit-card"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
                          </div>
                          <div>
                            <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.PAYMENT_GATEWAY_TITLE') }}</h3>
                            <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.PAYMENT_GATEWAY_DESC') }}</p>
                          </div>
                        </div>
                        <label class="inline-flex items-center cursor-pointer">
                          <input type="checkbox" v-model="paymentMethods.paymentGateway" :disabled="isSaving" class="sr-only peer">
                          <div
                            class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                          </div>
                        </label>
                      </div>
                      
                      <div 
                        v-if="paymentMethods.paymentGateway" 
                        class="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 transition-all duration-200 ease-in-out"
                      >
                        <!-- Coming Soon -->
                        <!-- <div class="flex items-center justify-center py-8">
                          <div class="text-center">
                            <div class="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12,6 12,12 16,14"/>
                              </svg>
                            </div>
                            <h4 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Coming Soon</h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Payment gateway configuration will be available soon.</p>
                          </div>
                        </div> -->
  
                        <!-- DONT DELETE! -->
                        
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.PROVIDER_LABEL') }} <span class="text-red-500">*</span>
                          </label>
                          <select 
                            v-model="paymentGateway.provider"
                            class="w-full mb-0 p-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value="">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.SELECT_PROVIDER') }}</option>
                            <option v-for="provider in paymentGatewayProviders" :key="provider.id" :value="provider.id">
                              {{ provider.label }}
                            </option>
                          </select>
                        </div>
                        
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            API Key <span class="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            v-model="paymentGateway.apiKey"
                            :placeholder="$t('AGENT_MGMT.SALESBOT.PAYMENT.API_KEY_PLACEHOLDER')"
                            class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                          />
                        </div>
                        
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {{ $t('AGENT_MGMT.SALESBOT.PAYMENT.MERCHANT_CODE_LABEL') }} <span class="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            v-model="paymentGateway.merchantCode"
                            :placeholder="$t('AGENT_MGMT.SALESBOT.PAYMENT.MERCHANT_CODE_PLACEHOLDER')"
                            class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                          />
                        </div>
                       
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="w-[240px] flex flex-col gap-3">
                <div class="sticky top-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-10 h-10 flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-credit-card-icon lucide-credit-card w-5 h-5 text-green-600 dark:text-green-400"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-700 dark:text-slate-300">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.HEADER') }}</h3>
                      <p class="text-sm text-slate-500 dark:text-slate-400">{{ $t('AGENT_MGMT.SALESBOT.PAYMENT.HEADER_DESC') }}</p>
                    </div>
                  </div>
                  
                  <Button
                    class="w-full"
                    :is-loading="isSaving"
                    :disabled="isSaving"
                    @click="() => submitPaymentConfig()"
                  >
                    <span class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {{ $t('AGENT_MGMT.FORM_CREATE.SUBMIT') }}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Cart Configuration Tab -->
          <div v-show="activeTabIndex === 5" class="w-full min-w-0">
            <div class="flex flex-row gap-4">
              <div class="flex-1 min-w-0 flex flex-col justify-stretch gap-6">
                <div class="space-y-4">
                  <div>
                    <label class="block font-medium mb-1">{{ $t('AGENT_MGMT.SALESBOT.CART.HEADER') }}</label>
  
                    <!-- Cart Toggle -->
                    <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                      <div class="flex items-center justify-between p-4">
                        <div class="flex items-center">
                          <div class="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-white lucide lucide-shopping-cart">
                              <circle cx="8" cy="21" r="1"/>
                              <circle cx="19" cy="21" r="1"/>
                              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
                            </svg> 
                          </div>
                          <div>
                            <h3 class="font-medium">{{ $t('AGENT_MGMT.SALESBOT.CART.ENABLE_TITLE') }}</h3>
                            <p class="text-sm text-gray-500 mt-1">{{ $t('AGENT_MGMT.SALESBOT.CART.ENABLE_DESC') }}</p>
                          </div>
                        </div>
                        <label class="inline-flex items-center cursor-pointer">
                          <input type="checkbox" v-model="cartEnabled" :disabled="isSaving" class="sr-only peer">
                          <div
                            class="border solid w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 relative after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                          </div>
                        </label>
                      </div>
                      
                      <!-- Cart Status Info -->
                      <div class="border-t border-gray-200 dark:border-gray-700 p-4">
                        <div v-if="cartEnabled" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                          <div class="flex items-start">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-green-600 dark:stroke-green-400 mt-0.5 mr-2 flex-shrink-0">
                              <path d="M9 12l2 2 4-4"/>
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                            <div>
                              <p class="text-sm font-medium text-green-800 dark:text-green-200">
                                {{ $t('AGENT_MGMT.SALESBOT.CART.ENABLED_STATUS') }}
                              </p>
                              <p class="text-sm text-green-600 dark:text-green-300 mt-1">
                                {{ $t('AGENT_MGMT.SALESBOT.CART.ENABLED_DESC_DETAIL') }}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div v-else class="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                          <div class="flex items-start">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stroke-gray-500 dark:stroke-gray-400 mt-0.5 mr-2 flex-shrink-0">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="15" y1="9" x2="9" y2="15"/>
                              <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <div>
                              <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {{ $t('AGENT_MGMT.SALESBOT.CART.DISABLED_STATUS') }}
                              </p>
                              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {{ $t('AGENT_MGMT.SALESBOT.CART.DISABLED_DESC_DETAIL') }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="w-[240px] flex flex-col gap-3">
                <div class="sticky top-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-10 h-10 flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart w-5 h-5 text-green-600 dark:text-green-400">
                          <circle cx="8" cy="21" r="1"/>
                          <circle cx="19" cy="21" r="1"/>
                          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
                        </svg>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-700 dark:text-slate-300">{{ $t('AGENT_MGMT.SALESBOT.CART.HEADER') }}</h3>
                      <p class="text-sm text-slate-500 dark:text-slate-400">{{ $t('AGENT_MGMT.SALESBOT.CART.HEADER_DESC') }}</p>
                    </div>
                  </div>
                  
                  <Button
                    class="w-full"
                    :is-loading="isSaving"
                    :disabled="isSaving"
                    @click="() => submitCartConfig()"
                  >
                    <span class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {{ $t('AGENT_MGMT.FORM_CREATE.SUBMIT') }}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <!-- QnA Tab Content -->
          <div v-show="activeTabIndex === 6" class="w-full min-w-0">
            <QnaKnowledgeSources
              :data="data"
              context="sales"
            />
          </div>

          <!-- Custom Numbering Content -->
          <div v-show="activeTabIndex === 7" class="w-full">
            <CustomNumberingTab :data="data" numbering-key="sales" />
          </div>

          <!-- Reminder Settings Content -->
          <div v-show="activeTabIndex === 8" class="w-full">
            <ReminderSettings
              v-if="data?.id"
              :ai-agent-id="data.id"
            />
          </div>

        </div>
      </div>
    </div>
  </div>
  <div v-if="showRegenerateModal" class="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" role="dialog">
    <div class="fixed inset-0 bg-slate-900/50 transition-opacity" @click="showRegenerateModal = false"></div>

    <div class="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-slate-800 p-6 text-left shadow-xl transition-all border border-slate-200 dark:border-slate-700">
      
      <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/20 mb-4">
        <svg class="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      </div>

      <div class="text-center">
        <h3 class="text-lg font-semibold leading-6 text-slate-900 dark:text-white" id="modal-title">
          {{ $t('AGENT_MGMT.EOBOT.REGENERATE_SHEETS') }}
        </h3>
        <div class="mt-2">
          <p class="text-sm text-slate-500 dark:text-slate-400">
            {{ $t('AGENT_MGMT.EOBOT.REGENERATE_SHEETS_DESC') }}
          </p>
        </div>
      </div>

      <div class="mt-6 flex flex-col sm:flex-row-reverse gap-3">
        <button 
          type="button" 
          class="inline-flex w-full justify-center bg-green-600 text-white rounded-md hover:bg-green-700 px-3 py-2 text-sm font-semibold shadow-sm sm:w-auto transition-colors"
          @click="regenerateSheetsInput"
        >
          {{ $t('AGENT_MGMT.EOBOT.REGENERATE_SHEETS_BTN') }}
        </button>
        <button 
          type="button" 
          class="inline-flex w-full justify-center rounded-lg bg-white dark:bg-transparent px-3 py-2 text-sm font-semibold text-slate-900 dark:text-slate-300 shadow-sm ring-1 ring-inset ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 sm:w-auto transition-colors"
          @click="showRegenerateModal = false"
        >
          {{ $t('AGENT_MGMT.EOBOT.REGENERATE_SHEETS_CANCEL') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete QRIS Confirmation Modal -->
  <woot-delete-modal
    v-if="showDeleteQrisModal"
    v-model:show="showDeleteQrisModal"
    :on-close="() => { showDeleteQrisModal = false; }"
    :on-confirm="confirmDeleteQrisImage"
    :title="$t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.DELETE_MODAL.TITLE')"
    :message="$t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.DELETE_MODAL.MESSAGE')"
    :confirm-text="$t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.DELETE_MODAL.CONFIRM_TEXT')"
    :reject-text="$t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.DELETE_MODAL.REJECT_TEXT')"
  />
</template>

<script setup>
import FileKnowledgeSources from '../knowledge-sources/FileKnowledgeSources.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import provinsiJson from '../wilayah/provinsi/provinsi.json';
import QnaKnowledgeSources from '../knowledge-sources/QnaKnowledgeSources.vue';
import { ref, reactive, watch, onMounted, computed, provide } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n'

// Google Sheets Auth Flow for Catalog
import googleSheetsExportAPI from '../../../../api/googleSheetsExport';
// AI Agents API
import aiAgents from '../../../../api/aiAgents';
import uploadAPI from '../../../../api/upload';
import idleConfigsAPI from '../../../../api/idleConfigs';
import shippingStoresAPI from '../../../../api/shippingStores';
import { useAlert } from 'dashboard/composables';
import CustomNumberingTab from './cs-bot-tabs/CustomNumberingTab.vue';
import ShippingConfig from './sales-bot-tabs/ShippingConfig.vue';
import NotificationSettings from './notification-settings/NotificationSettings.vue';
import ReminderSettings from './reminder-settings/ReminderSettings.vue';

const { t } = useI18n();
const store = useStore();

// Props for data from parent component
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  googleSheetsAuth: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:data']);
provide('emitUpdate', () => emit('update:data'));

// temperature bot
const creativityLevel = ref(0.3);
const creativityOptions = computed(() => [
  { label: t('AGENT_MGMT.CREATIVITY.DETERMINISTIC'), value: 0 },
  { label: t('AGENT_MGMT.CREATIVITY.CONSERVATIVE'), value: 0.1 },
  { label: t('AGENT_MGMT.CREATIVITY.NATURAL'), value: 0.3 },
  { label: t('AGENT_MGMT.CREATIVITY.INNOVATIVE'), value: 0.5 },
  { label: t('AGENT_MGMT.CREATIVITY.VISIONARY'), value: 0.7 },
]);

// idle time
const idleConfig = reactive({
  enabled: true,
  duration: window.chatwootConfig?.idleConversationDuration || 30,
});

// reminder proactive toggle
const reminderOfferEnabled = ref(false);

// delay message toggle
const isDelayEnabled = ref(false);
const debounceInterval = ref(10);

const delayOptions = [
  { value: 10, label: '10 detik' },
  { value: 20, label: '20 detik' },
  { value: 30, label: '30 detik' },
  { value: 40, label: '40 detik' },
  { value: 50, label: '50 detik' },
  { value: 60, label: '60 detik' },
];

// Helper function to get agent ID by type
function getAgentIdByType(type) {
  const flowData = props.data?.display_flow_data;
  if (!flowData?.agents_config) return null;
  
  const agent = flowData.agents_config.find(config => config.type === type);
  return agent?.agent_id || null;
}

function getCollectionNameByAgentType(type) {
  const flowData = props.data?.display_flow_data;
  if (!flowData?.agents_config) return null;

  const agent = flowData.agents_config.find(config => config.type === type);
  return agent?.collection_name || null;
}

// Computed property to get sales agent ID
const salesAgentId = computed(() => {
  return getAgentIdByType('sales');
});

const collectionName = computed(() => {
  return getCollectionNameByAgentType('sales');
});

// Load idle config from API
async function loadIdleConfig() {
  if (!props.data?.id) return;
  try {
    const response = await idleConfigsAPI.getConfig(props.data.id);
    if (response.data) {
      idleConfig.enabled = response.data.enabled !== undefined ? response.data.enabled : true;
      idleConfig.duration = response.data.duration || 30;
    }
  } catch (error) {
    console.error('Failed to load idle config:', error);
  }
}
// Initialize and load provinces on mount
onMounted(async () => {
  // Load saved configuration first
  loadSavedConfiguration();
  loadIdleConfig();
  
  // Load provinces for address selection
  loadProvinsi();

  if (props.data?.id) {
    await store.dispatch('shippingStores/fetch', props.data.id);
  }
  // Pre-load Google Maps API but don't initialize map yet
  try {
    await loadGoogleMaps();
  } catch (error) {
    // Try alternative loading method
    setTimeout(async () => {
      try {
        // Check if Google is now available
        if (window.google && window.google.maps && window.google.maps.Map) {
          // Google Maps available after retry
        } else {
          // Google Maps still not available, will retry on map initialization
        }
      } catch (retryError) {
        // Retry loading also failed
      }
    }, 2000);
  }
});

// Watch for props data changes AFTER initial mount to reload configuration
// Note: No 'immediate: true' to avoid double-loading on mount (onMounted already calls it)
watch(
  () => props.data,
  async (newData) => {
    if (newData && newData.display_flow_data) {
      loadSavedConfiguration();
      loadIdleConfig();
      if (newData.id) {
        await store.dispatch('shippingStores/fetch', newData.id);
      }
    }
  },
  { deep: true }
);

const catalogStep = computed(() => {
  // If authenticated but no sales spreadsheets yet, show 'connected' step to allow creating sheets
  if (props.googleSheetsAuth.step === 'sheetConfig') {
    const salesSheets = props.googleSheetsAuth.spreadsheetUrls.sales;
    if (!salesSheets?.input && !salesSheets?.output) {

      return 'connected';
    }
  }
  return props.googleSheetsAuth.step;
});

const catalogLoading = computed(() => props.googleSheetsAuth.loading);
const catalogAccount = computed(() => props.googleSheetsAuth.account);
const catalogSheets = computed(() => props.googleSheetsAuth.spreadsheetUrls.sales);

// Computed property to determine if we should show auth error for sales specifically
const salesAuthError = computed(() => {
  // Only show auth error if it's really an auth problem, not just missing spreadsheets
  const error = props.googleSheetsAuth.error;
  if (!error) return null;
  
  // Don't show error if it's just about missing spreadsheets for new agents
  if (error.includes('not found') || error.includes('404')) {
    return null;
  }
  
  // Show error for actual auth/permission issues
  if (error.includes('authentication') || error.includes('permission') || error.includes('unauthorized')) {
    return error;
  }
  
  return null; // Don't show other types of errors in the UI
});

const notification = ref(null);

const salesCategories = computed(() => [
  { key: 'cart_created', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.CART_CREATED') },
  { key: 'cart_updated', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.CART_UPDATED') },
  { key: 'cart_deleted', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.CART_DELETED') },
  { key: 'order_created', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.ORDER_CREATED') },
  { key: 'order_updated', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.ORDER_UPDATED') },
  { key: 'order_canceled', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.ORDER_CANCELED') },
  { key: 'payment_created', label: t('AGENT_MGMT.SALESBOT.NOTIFICATION.CATEGORIES.PAYMENT_CREATED') },
]);

const salesVariableConfig = computed(() => ({
  helpKey: 'AGENT_MGMT.SALESBOT.NOTIFICATION.TEMPLATE_HELP',
  variables: [
    {
      labelKey: 'AGENT_MGMT.NOTIFICATION.VAR_CONTENT_SUMMARY',
      value: '{{content_summary}}',
      example: `ID Pesanan: SO-001/01/2026
Nama Pelanggan: Budi Santoso
No. Telp: 62812345678901
Produk: Paket Premium A
Jumlah: 2
Total: Rp 500,000
Metode Pembayaran: Bank Transfer
Status: Menunggu Pembayaran
Catatan: Kirim sebelum jam 5 sore`,
    },
  ],
}));

const productColumns = ref('sku,name,unit_price,quantity,deskripsi');
const syncingColumns = ref(false);
const authError = computed(() => props.googleSheetsAuth.error);
watch(salesAuthError, (newError) => {
  if (newError) {
    notification.value = { message: t('AGENT_MGMT.AUTH_ERROR'), type: 'error' };
  }
  else {
    notification.value = null;
  }
}, { immediate: true });

const showRegenerateModal = ref(false);
const isRegenerating = ref(false);
const showDeleteQrisModal = ref(false);

function openRegenerateModal() {
  showRegenerateModal.value = true;
}

async function regenerateSheetsInput() {
  showRegenerateModal.value = false;

  try {
    isRegenerating.value = true;
    const flowData = props.data.display_flow_data;

    const payload = {
      account_id: parseInt(flowData.account_id, 10),
      agent_id: salesAgentId.value,
      type: 'sales',
    };

    // Memanggil API wrapper yang baru kita perbaiki
    const response = await googleSheetsExportAPI.regenerateSpreadsheet(payload);

    if (response.data && response.data.input_spreadsheet_url) {
        props.googleSheetsAuth.spreadsheetUrls.sales.input = response.data.input_spreadsheet_url;

        if (response.data.output_spreadsheet_url) {
            props.googleSheetsAuth.spreadsheetUrls.sales.output = response.data.output_spreadsheet_url;
        }

        showNotification('Input spreadsheet berhasil dibuat ulang!', 'success');
    } else {
        throw new Error("Respon server tidak memiliki URL spreadsheet baru.");
    }

  } catch (error) {
    console.error('Failed to regenerate sheet:', error);
    showNotification('Gagal membuat ulang spreadsheet. Silakan coba lagi.', 'error');
  } finally {
    isRegenerating.value = false;
  }
}

function retryAuthentication() {
  connectGoogle();
  // Note: salesAuthError is computed, so error will clear automatically when auth succeeds
}

function disconnectGoogle() {
  console.log('Disconnect Google account clicked');
  googleSheetsExportAPI.disconnectAccount()
    .then(() => {
      props.googleSheetsAuth.step = 'auth';
      props.googleSheetsAuth.account = null;
      props.googleSheetsAuth.spreadsheetUrls.sales = { input: null, output: null };
      props.googleSheetsAuth.error = null;
      showNotification('Disconnected from Google successfully.', 'success');
    })
    .catch((error) => {
      console.error('Failed to disconnect Google account:', error);
      showNotification('Failed to disconnect Google account. Please try again.', 'error');
    });
}

function showNotification(message, type = 'success') {
  notification.value = { message, type };
  setTimeout(() => {
    notification.value = null;
  }, 3000);
}

async function connectGoogle() {
  try {
    props.googleSheetsAuth.loading = true;
    const response = await googleSheetsExportAPI.getAuthorizationUrl();
    if (response.data.authorization_url) {
      showNotification('Opening Google authentication in a new tab...', 'info');
      window.location.href = response.data.authorization_url;
      // window.open(response.data.authorization_url, '_blank', 'noopener,noreferrer')
    } else {
      showNotification(
        'Failed to get authorization URL. Please check backend logs.',
        'error'
      );
    }
  } catch (error) {
    showNotification('Authentication failed. Please try again.', 'error');
  } finally {
    props.googleSheetsAuth.loading = false;
  }
}

async function createSheets() {
  props.googleSheetsAuth.loading = true;
  try {
    const flowData = props.data.display_flow_data;
    const payload = {
      account_id: parseInt(flowData.account_id, 10),
      agent_id: salesAgentId.value,
      type: 'sales',
    };

    const response = await googleSheetsExportAPI.createSpreadsheet(payload);

    props.googleSheetsAuth.spreadsheetUrls.sales.input = response.data.input_spreadsheet_url;
    props.googleSheetsAuth.spreadsheetUrls.sales.output = response.data.output_spreadsheet_url;
    props.googleSheetsAuth.step = 'sheetConfig';
    showNotification('catalog output sheet created successfully!', 'success')
  } catch (error) {
    // eslint-disable-next-line no-console
    props.googleSheetsAuth.loading = false;
    showNotification(
      'Failed to create catalog sheet. Please try again.',
      'error'
    );
  } finally {
    props.googleSheetsAuth.loading = false;
  }
}

async function syncProductColumns() {
  try {
    syncingColumns.value = true;
    showNotification(t('AGENT_MGMT.SALESBOT.CATALOG.SYNC_INFO'), 'info');
    const flowData = props.data.display_flow_data;
    const payload = {
      account_id: parseInt(flowData.account_id, 10),
      agent_id: salesAgentId.value,
      type: 'sales',
      collection_name: collectionName.value,
    };
    const syncDataResponse = await googleSheetsExportAPI.syncSpreadsheet(payload);
    // console.log('Sync response:', syncDataResponse.data.data);

    if (!syncDataResponse.data.data.success) {
      showNotification(t('AGENT_MGMT.SALESBOT.CATALOG.SYNC_ERROR'), 'error');
      return;
    }
    const message = t('AGENT_MGMT.SALESBOT.CATALOG.SYNC_SUCCESS');
    
    showNotification(message, 'success');
  } catch (error) {
    console.error('Sync error:', error);
    showNotification(t('AGENT_MGMT.SALESBOT.CATALOG.SYNC_ERROR'), 'error');
  } finally {
    syncingColumns.value = false;
  }
}

const tabs = computed(() => [
  {
    key: '0',
    index: 0,
    name: t('AGENT_MGMT.SALESBOT.CATALOG.HEADER'),
    icon: 'i-lucide-package',
  },
  {
    key: '1',
    index: 1,
    name: 'Pengaturan',
    icon: 'i-lucide-settings',
  },
  {
    key: '2',
    index: 2,
    name: t('AGENT_MGMT.BOOKING_BOT.FILE_TAB'),
    icon: 'i-lucide-folder',
  },
  {
    key: '3',
    index: 3,
    name: t('AGENT_MGMT.SALESBOT.SHIPPING.HEADER'),
    icon: 'i-lucide-truck',
  },
  {
    key: '4',
    index: 4,
    name: t('AGENT_MGMT.SALESBOT.PAYMENT.HEADER'),
    icon: 'i-lucide-credit-card',
  },
  {
    key: '5',
    index: 5,
    name: t('AGENT_MGMT.SALESBOT.CART.HEADER'),
    icon: 'i-lucide-shopping-cart',
  },
  {
    key: '6',
    index: 6,
    name: 'QnA',
    icon: 'i-lucide-help-circle',
  },
  {
    key: '7',
    index: 7,
    name: 'Penomoran Otomatis',
    icon: 'i-lucide-notebook-tabs',
  },
  {
    key: '8',
    index: 8,
    name: t('AGENT_MGMT.REMINDER.MANAGEMENT.HEADER'),
    icon: 'i-lucide-bell',
  },
])
const activeTabIndex = ref(0);
const catalogSheet = ref('');
const catalogDesc = ref('');
const cartEnabled = ref(false);

const shippingMethods = reactive({
  kurirToko: false,
  kurirBiasa: false,
  ambilToko: false
});

const kurirToko = reactive({ 
  alamat: '', 
  radius: '', 
  wilayah: '', 
  kotaWilayah: '', // For storing city/district selection in region type
  serviceAreaType: 'radius', // 'radius' or 'region'
  pricingMethod: 'flatRate', // 'flatRate' or 'perDistance'
  flatRate: '',
  biayaPerJarak: '',
  gratisOngkir: false,
  latitude: -6.2088, // Default to Jakarta
  longitude: 106.8456,
  mapLoaded: false,
  minimalBelanja: '',
  estimasi: ''
});
const kurirBiasa = reactive({ 
  provinsi: '', 
  kota: '', 
  kecamatan: '',
  kelurahan: '', 
  jalan: '', 
  kodePos: '',
  kurir: [],
  shippingCosts: {}
});

// RajaOngkir API Configuration
const RAJA_ONGKIR_API_KEY = 'YOUR_RAJAONGKIR_API_KEY'; // Replace with your actual API key
const RAJA_ONGKIR_BASE_URL = 'https://api.rajaongkir.com/starter'; // Use 'pro' for pro account

// Location data from RajaOngkir API
const provinsiOptions = ref([]);
const kotaOptions = ref([]);
const kecamatanOptions = ref([]);
const kelurahanOptions = ref([]);
const loadingKelurahan = ref(false);

// Service area specific options
const serviceAreaKotaOptions = ref([]);

// Load kelurahan/desa from JSON based on selected province, kabupaten/kota, and kecamatan
const loadKelurahan = async (provinceId, kabupatenId, kecamatanId) => {
  loadingKelurahan.value = true;
  try {
    const kelurahanModule = await import(
      `../wilayah/kelurahan_desa/keldesa-${provinceId}-${kabupatenId}-${kecamatanId}.json`
    );
    const kelurahanJson = kelurahanModule.default || kelurahanModule;
    kelurahanOptions.value = Object.entries(kelurahanJson).map(([id, name]) => ({ id, name }));
  } catch (error) {
    showNotification('Failed to load kelurahan data', 'error');
    kelurahanOptions.value = [];
  } finally {
    loadingKelurahan.value = false;
  }
};
const loadingProvinsi = ref(false);
const loadingKota = ref(false);
const loadingKecamatan = ref(false);
const loadingShippingCost = ref(false);

// Search functionality for dropdowns
const provinsiSearchQuery = ref('');
const kotaSearchQuery = ref('');
const kecamatanSearchQuery = ref('');
const isProvinsiDropdownOpen = ref(false);
const isKotaDropdownOpen = ref(false);
const isKecamatanDropdownOpen = ref(false);
const provinsiDropdownRef = ref(null);
const kotaDropdownRef = ref(null);
const kecamatanDropdownRef = ref(null);
const kelurahanSearchQuery = ref('');
const isKelurahanDropdownOpen = ref(false);
const kelurahanDropdownRef = ref(null);

// Service Area dropdown variables
const serviceAreaProvinsiSearchQuery = ref('');
const isServiceAreaProvinsiDropdownOpen = ref(false);
const serviceAreaDropdownRef = ref(null);

// Service Area city dropdown variables
const serviceAreaKotaSearchQuery = ref('');
const isServiceAreaKotaDropdownOpen = ref(false);
const serviceAreaKotaDropdownRef = ref(null);
const loadingServiceAreaKota = ref(false);

// Computed properties for filtered options
const filteredProvinsiOptions = computed(() => {
  if (!provinsiSearchQuery.value) {
    return provinsiOptions.value;
  }
  return provinsiOptions.value.filter(provinsi => 
    provinsi.name.toLowerCase().includes(provinsiSearchQuery.value.toLowerCase())
  );
});

const filteredKotaOptions = computed(() => {
  if (!kotaSearchQuery.value) {
    return kotaOptions.value;
  }
  return kotaOptions.value.filter(kota => 
    kota.name.toLowerCase().includes(kotaSearchQuery.value.toLowerCase())
  );
});

const filteredKecamatanOptions = computed(() => {
  if (!kecamatanSearchQuery.value) {
    return kecamatanOptions.value;
  }
  return kecamatanOptions.value.filter(kecamatan => 
    kecamatan.name.toLowerCase().includes(kecamatanSearchQuery.value.toLowerCase())
  );
});

const filteredKelurahanOptions = computed(() => {
  if (!kelurahanSearchQuery.value) {
    return kelurahanOptions.value;
  }
  return kelurahanOptions.value.filter(kelurahan => 
    kelurahan.name.toLowerCase().includes(kelurahanSearchQuery.value.toLowerCase())
  );
});

// Computed properties for selected names
const selectedProvinsiName = computed(() => {
  const selected = provinsiOptions.value.find(p => p.id === kurirBiasa.provinsi);
  return selected ? selected.name : '';
});

const selectedKotaName = computed(() => {
  const selected = kotaOptions.value.find(k => k.id === kurirBiasa.kota);
  return selected ? selected.name : '';
});

const selectedKecamatanName = computed(() => {
  const selected = kecamatanOptions.value.find(k => k.id === kurirBiasa.kecamatan);
  return selected ? selected.name : '';
});

const selectedKelurahanName = computed(() => {
  const selected = kelurahanOptions.value.find(k => k.id === kurirBiasa.kelurahan);
  return selected ? selected.name : '';
});

// Service Area computed properties
const filteredServiceAreaProvinsiOptions = computed(() => {
  if (!serviceAreaProvinsiSearchQuery.value) {
    return provinsiOptions.value;
  }
  return provinsiOptions.value.filter(provinsi => 
    provinsi.name.toLowerCase().includes(serviceAreaProvinsiSearchQuery.value.toLowerCase())
  );
});

const filteredServiceAreaKotaOptions = computed(() => {
  if (!serviceAreaKotaSearchQuery.value) {
    return serviceAreaKotaOptions.value;
  }
  return serviceAreaKotaOptions.value.filter(kota => 
    kota.name.toLowerCase().includes(serviceAreaKotaSearchQuery.value.toLowerCase())
  );
});

const selectedServiceAreaProvinsiName = computed(() => {
  const selected = provinsiOptions.value.find(p => p.id === kurirToko.wilayah);
  return selected ? selected.name : '';
});

const selectedServiceAreaKotaName = computed(() => {
  const selected = serviceAreaKotaOptions.value.find(k => k.id === kurirToko.kotaWilayah);
  return selected ? selected.name : '';
});


// RajaOngkir API Headers
const getRajaOngkirHeaders = () => ({
  'key': RAJA_ONGKIR_API_KEY,
  'content-type': 'application/x-www-form-urlencoded'
});

// Load provinces from RajaOngkir
const loadProvinsi = async () => {
  loadingProvinsi.value = true;
  try {
    // Use provinsi.json as the source
    provinsiOptions.value = Object.entries(provinsiJson).map(([id, name]) => ({ id, name }));
  } catch (error) {
    showNotification('Failed to load provinces data', 'error');
    provinsiOptions.value = [];
  } finally {
    loadingProvinsi.value = false;
    
  }
};

// Load cities/regencies from RajaOngkir based on selected province
const loadKota = async (provinceId) => {
  loadingKota.value = true;
  try {
    // Dynamically import kabupaten/kota JSON based on provinceId
    const kabupatenModule = await import(
      `../wilayah/kabupaten_kota/kab-${provinceId}.json`
    );
    const kabupatenJson = kabupatenModule.default || kabupatenModule;
    kotaOptions.value = Object.entries(kabupatenJson).map(([id, name]) => ({ id, name }));
  } catch (error) {
    showNotification('Failed to load cities data', 'error');
    kotaOptions.value = [];
  } finally {
    loadingKota.value = false;
  }
};

// Calculate shipping cost using RajaOngkir
const calculateShippingCost = async (origin, destination, weight, courier) => {
  try {
    loadingShippingCost.value = true;
    const formData = new FormData();
    formData.append('origin', origin);
    formData.append('destination', destination);
    formData.append('weight', weight);
    formData.append('courier', courier.toLowerCase());

    const response = await fetch(`${RAJA_ONGKIR_BASE_URL}/cost`, {
      method: 'POST',
      headers: {
        'key': RAJA_ONGKIR_API_KEY
      },
      body: formData
    });
    
    const data = await response.json();
    
    if (data.rajaongkir.status.code === 200) {
      return data.rajaongkir.results[0]?.costs || [];
    } else {
      throw new Error(data.rajaongkir.status.description);
    }
  } catch (error) {
    showNotification('Failed to calculate shipping cost', 'error');
    return [];
  } finally {
    loadingShippingCost.value = false;
  }
};

// Load districts from RajaOngkir based on selected city/regency  
const loadKecamatan = async (provinceId, kabupatenId) => {
  loadingKecamatan.value = true;
  try {
    // Dynamically import kecamatan JSON based on provinceId and kabupatenId
    const kecamatanModule = await import(
      `../wilayah/kecamatan/kec-${provinceId}-${kabupatenId}.json`
    );
    const kecamatanJson = kecamatanModule.default || kecamatanModule;
    kecamatanOptions.value = Object.entries(kecamatanJson).map(([id, name]) => ({ id, name }));
  } catch (error) {
    showNotification('Failed to load districts data', 'error');
    kecamatanOptions.value = [];
  } finally {
    loadingKecamatan.value = false;
  }
};

async function onProvinsiChange() {
  kurirBiasa.kota = '';
  kurirBiasa.kecamatan = '';
  kurirBiasa.kodePos = '';
  kotaOptions.value = [];
  kecamatanOptions.value = [];
  
  if (kurirBiasa.provinsi) {
    await loadKota(kurirBiasa.provinsi);
  }
}

async function onKotaChange() {
  kurirBiasa.kecamatan = '';
  kurirBiasa.kodePos = '';
  kecamatanOptions.value = [];
  
  // Auto-fill postal code from RajaOngkir city data
  if (kurirBiasa.kota) {
    const selectedKota = kotaOptions.value.find(kota => kota.id === kurirBiasa.kota);
    if (selectedKota && selectedKota.postal_code) {
      kurirBiasa.kodePos = selectedKota.postal_code;
    }
    // Load kecamatan using province and kabupaten/kota id
    await loadKecamatan(kurirBiasa.provinsi, kurirBiasa.kota);
  }
}

function onKecamatanChange() {
  kelurahanOptions.value = [];
  kurirBiasa.kelurahan = ''; // Reset kelurahan selection
  if (kurirBiasa.kecamatan) {
    loadKelurahan(kurirBiasa.provinsi, kurirBiasa.kota, kurirBiasa.kecamatan);
  }

}

// Search dropdown functions
function toggleProvinsiDropdown() {
  if (!loadingProvinsi.value) {
    isProvinsiDropdownOpen.value = !isProvinsiDropdownOpen.value;
  }
}

function toggleKotaDropdown() {
  if (!loadingKota.value && kurirBiasa.provinsi) {
    isKotaDropdownOpen.value = !isKotaDropdownOpen.value;
  }
}

function toggleKecamatanDropdown() {
  if (!loadingKecamatan.value && kurirBiasa.kota) {
    isKecamatanDropdownOpen.value = !isKecamatanDropdownOpen.value;
  }
}

function toggleKelurahanDropdown() {
  if (!loadingKelurahan.value && kurirBiasa.kecamatan) {
    isKelurahanDropdownOpen.value = !isKelurahanDropdownOpen.value;
  }
}

function onProvinsiSearch() {
  isProvinsiDropdownOpen.value = true;
}

function onKotaSearch() {
  if (kurirBiasa.provinsi) {
    isKotaDropdownOpen.value = true;
  }
}

function onKecamatanSearch() {
  if (kurirBiasa.kota) {
    isKecamatanDropdownOpen.value = true;
  }
}

function onKelurahanSearch() {
  if (kurirBiasa.kecamatan) {
    isKelurahanDropdownOpen.value = true;
  }
}

// Service Area dropdown functions
function toggleServiceAreaProvinsiDropdown() {
  if (!loadingProvinsi.value && kurirToko.serviceAreaType === 'region') {
    isServiceAreaProvinsiDropdownOpen.value = !isServiceAreaProvinsiDropdownOpen.value;
  }
}

function toggleServiceAreaKotaDropdown() {
  if (!loadingServiceAreaKota.value && kurirToko.serviceAreaType === 'region' && kurirToko.wilayah) {
    isServiceAreaKotaDropdownOpen.value = !isServiceAreaKotaDropdownOpen.value;
  }
}

function onServiceAreaProvinsiSearch() {
  if (kurirToko.serviceAreaType === 'region') {
    isServiceAreaProvinsiDropdownOpen.value = true;
  }
}

function onServiceAreaKotaSearch() {
  if (kurirToko.serviceAreaType === 'region' && kurirToko.wilayah) {
    isServiceAreaKotaDropdownOpen.value = true;
  }
}

function selectServiceAreaProvinsi(provinsi) {
  kurirToko.wilayah = provinsi.id;
  kurirToko.kotaWilayah = ''; // Reset city selection when province changes
  serviceAreaProvinsiSearchQuery.value = '';
  isServiceAreaProvinsiDropdownOpen.value = false;
  serviceAreaKotaOptions.value = []; // Clear city options
  // Load cities for selected province
  loadServiceAreaKota(provinsi.id);
}

function selectServiceAreaKota(kota) {
  kurirToko.kotaWilayah = kota.id;
  serviceAreaKotaSearchQuery.value = '';
  isServiceAreaKotaDropdownOpen.value = false;
}

// Load cities for service area based on selected province
const loadServiceAreaKota = async (provinceId) => {
  loadingServiceAreaKota.value = true;
  try {
    // Dynamically import kabupaten/kota JSON based on provinceId
    const kabupatenModule = await import(
      `../wilayah/kabupaten_kota/kab-${provinceId}.json`
    );
    const kabupatenJson = kabupatenModule.default || kabupatenModule;
    serviceAreaKotaOptions.value = Object.entries(kabupatenJson).map(([id, name]) => ({ id, name }));
  } catch (error) {
    showNotification('Failed to load cities data for service area', 'error');
    serviceAreaKotaOptions.value = [];
  } finally {
    loadingServiceAreaKota.value = false;
  }
};

function selectKota(kota) {
  kurirBiasa.kota = kota.id;
  kotaSearchQuery.value = '';
  isKotaDropdownOpen.value = false;
  onKotaChange();
}

function selectKecamatan(kecamatan) {
  kurirBiasa.kecamatan = kecamatan.id;
  kecamatanSearchQuery.value = '';
  isKecamatanDropdownOpen.value = false;
  onKecamatanChange();
}

function selectKelurahan(kelurahan) {
  kurirBiasa.kelurahan = kelurahan.id;
  kelurahanSearchQuery.value = '';
  isKelurahanDropdownOpen.value = false;
}

// Click outside handlers
function handleProvinsiClickOutside(event) {
  if (provinsiDropdownRef.value && !provinsiDropdownRef.value.contains(event.target)) {
    isProvinsiDropdownOpen.value = false;
  }
}

function handleKotaClickOutside(event) {
  if (kotaDropdownRef.value && !kotaDropdownRef.value.contains(event.target)) {
    isKotaDropdownOpen.value = false;
  }
}

function handleKecamatanClickOutside(event) {
  if (kecamatanDropdownRef.value && !kecamatanDropdownRef.value.contains(event.target)) {
    isKecamatanDropdownOpen.value = false;
  }
}

function handleKelurahanClickOutside(event) {
  if (kelurahanDropdownRef.value && !kelurahanDropdownRef.value.contains(event.target)) {
    isKelurahanDropdownOpen.value = false;
  }
}

function handleServiceAreaClickOutside(event) {
  if (serviceAreaDropdownRef.value && !serviceAreaDropdownRef.value.contains(event.target)) {
    isServiceAreaProvinsiDropdownOpen.value = false;
  }
}

function handleServiceAreaKotaClickOutside(event) {
  if (serviceAreaKotaDropdownRef.value && !serviceAreaKotaDropdownRef.value.contains(event.target)) {
    isServiceAreaKotaDropdownOpen.value = false;
  }
}

function selectProvinsi(provinsi) {
  kurirBiasa.provinsi = provinsi.id;
  provinsiSearchQuery.value = '';
  isProvinsiDropdownOpen.value = false;
  // Reset city/kabupaten and kecamatan selections
  kurirBiasa.kota = '';
  kurirBiasa.kecamatan = '';
  kotaOptions.value = [];
  kecamatanOptions.value = [];
  // Load cities/kabupaten for selected province
  loadKota(provinsi.id);
};

// Watch for dropdown open states to add/remove event listeners
watch(isProvinsiDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleProvinsiClickOutside);
  } else {
    document.removeEventListener('click', handleProvinsiClickOutside);
  }
});

watch(isKotaDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleKotaClickOutside);
  } else {
    document.removeEventListener('click', handleKotaClickOutside);
  }
});

watch(isKecamatanDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleKecamatanClickOutside);
  } else {
    document.removeEventListener('click', handleKecamatanClickOutside);
  }
});

watch(isKelurahanDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleKelurahanClickOutside);
  } else {
    document.removeEventListener('click', handleKelurahanClickOutside);
  }
});

watch(isServiceAreaProvinsiDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleServiceAreaClickOutside);
  } else {
    document.removeEventListener('click', handleServiceAreaClickOutside);
  }
});

watch(isServiceAreaKotaDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleServiceAreaKotaClickOutside);
  } else {
    document.removeEventListener('click', handleServiceAreaKotaClickOutside);
  }
});

// Reset search queries when selections change
watch(() => kurirBiasa.provinsi, () => {
  provinsiSearchQuery.value = '';
});

watch(() => kurirBiasa.kota, () => {
  kotaSearchQuery.value = '';
});

watch(() => kurirBiasa.kecamatan, () => {
  kecamatanSearchQuery.value = '';
});

watch(() => kurirBiasa.kelurahan, () => {
  kelurahanSearchQuery.value = '';
});

watch(() => kurirToko.wilayah, () => {
  serviceAreaProvinsiSearchQuery.value = '';
});

watch(() => kurirToko.kotaWilayah, () => {
  serviceAreaKotaSearchQuery.value = '';
});


// Calculate shipping costs for selected couriers
async function calculateShippingCosts() {
  if (!kurirBiasa.kota || kurirBiasa.kurir.length === 0) {
    showNotification('Please select destination city and courier services', 'error');
    return;
  }
  
  const weight = 1; // Default 1kg, configurable
  const origin = '501'; // Default origin city ID (Yogyakarta), configurable based on store location
  
  showNotification('Calculating shipping costs...', 'info');
  
  kurirBiasa.shippingCosts = {};
  
  for (const courier of kurirBiasa.kurir) {
    try {
      const costs = await calculateShippingCost(origin, kurirBiasa.kota, weight, courier);
      kurirBiasa.shippingCosts[courier] = costs;
    } catch (error) {
      kurirBiasa.shippingCosts[courier] = [];
    }
  }
  showNotification('Shipping costs calculated successfully', 'success');
}

// RajaOngkir supported couriers (Starter account)
const kurirOptions = [
  { label: 'JNE', id: 'jne' },
  { label: 'POS Indonesia', id: 'pos' },
  { label: 'TIKI', id: 'tiki' }
];
// For Pro account, you can add more:
// { label: 'Wahana', id: 'wahana' },
// { label: 'SiCepat', id: 'sicepat' },
// { label: 'J&T', id: 'jnt' },
// { label: 'Ninja Xpress', id: 'ninja' },
// { label: 'Lion Parcel', id: 'lion' },
const isKurirDropdownOpen = ref(false);
const kurirDropdownRef = ref(null);

function toggleKurir(kurirId) {
  const idx = kurirBiasa.kurir.indexOf(kurirId);
  if (idx > -1) {
    kurirBiasa.kurir.splice(idx, 1);
  } else {
    kurirBiasa.kurir.push(kurirId);
  }
}
function isKurirSelected(kurirId) {
  return kurirBiasa.kurir.includes(kurirId);
}

function handleKurirClickOutside(event) {
  if (kurirDropdownRef.value && !kurirDropdownRef.value.contains(event.target)) {
    isKurirDropdownOpen.value = false;
  }
}

watch(isKurirDropdownOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleKurirClickOutside);
  } else {
    document.removeEventListener('click', handleKurirClickOutside);
  }
});
const ambilToko = reactive({ 
  alamat: '', 
  jamBuka: '09:00', 
  jamTutup: '17:00', 
  estimasi: '' 
});

// Google Maps Integration
const mapRef = ref(null);
const mapInstance = ref(null);
const markerInstance = ref(null);
const geocoderInstance = ref(null);
const mapLoadingTimeout = ref(null);

// Google Maps API Integration
// NOTE: Replace with your actual Google Maps API key
const GOOGLE_MAPS_API_KEY = window.chatwootConfig?.googleMapsApiKey || '';
// Validate API key
const validateApiKey = () => {
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY') {
    return false;
  }
  return true;
};

// Load Google Maps API
const loadGoogleMaps = () => {
  return new Promise((resolve, reject) => {
    // Validate API key first
    if (!validateApiKey()) {
      reject(new Error('Google Maps API key is not configured'));
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.Map) {
      resolve(window.google); // Return the full google object
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          resolve(window.google); // Return the full google object
        } else {
          setTimeout(checkGoogleMaps, 100);
        }
      };
      checkGoogleMaps();
      return;
    }

    
    // Create a unique callback name to avoid conflicts
    const callbackName = `initGoogleMaps_${Date.now()}`;
    
    window[callbackName] = () => {
      if (window.google && window.google.maps && window.google.maps.Map) {
        resolve(window.google); // Return the full google object
      } else {
        reject(new Error('Google Maps API loaded but objects not available'));
      }
      delete window[callbackName];
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = (error) => {
      delete window[callbackName];
      reject(new Error('Failed to load Google Maps script - please check your API key'));
    };
    
    document.head.appendChild(script);
  });
};

// Initialize Google Maps
const initializeMap = async () => {
  
  if (!mapRef.value) {
    return;
  }
  
  if (kurirToko.mapLoaded) {
    return;
  }

  try {
    const google = await loadGoogleMaps();
    
    if (!google || !google.maps) {
      throw new Error('Google Maps API not properly loaded - missing maps object');
    }
    
    if (!google.maps.Map) {
      throw new Error('Google Maps API not properly loaded - missing Map constructor');
    }
    
    // Initialize map
    mapInstance.value = new google.maps.Map(mapRef.value, {
      zoom: 15,
      center: { lat: kurirToko.latitude, lng: kurirToko.longitude },
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Initialize marker
    markerInstance.value = new google.maps.Marker({
      position: { lat: kurirToko.latitude, lng: kurirToko.longitude },
      map: mapInstance.value,
      draggable: true,
      title: 'Store Location'
    });

    // Initialize geocoder
    geocoderInstance.value = new google.maps.Geocoder();

    // Add marker drag listener
    markerInstance.value.addListener('dragend', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      kurirToko.latitude = lat;
      kurirToko.longitude = lng;
      
      // Reverse geocode to update address
      reverseGeocode(lat, lng);
    });

    kurirToko.mapLoaded = true;
  } catch (error) {
    showNotification('Failed to load map. Please check your API key and internet connection.', 'error');
  }
};

// Geocode address to coordinates
const geocodeAddress = async (address) => {
  if (!geocoderInstance.value || !address.trim()) return;

  try {
    const response = await new Promise((resolve, reject) => {
      geocoderInstance.value.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          resolve(results[0]);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });

    const location = response.geometry.location;
    const lat = location.lat();
    const lng = location.lng();

    // Update coordinates
    kurirToko.latitude = lat;
    kurirToko.longitude = lng;

    // Update map center and marker position
    if (mapInstance.value && markerInstance.value) {
      const newPosition = { lat, lng };
      mapInstance.value.setCenter(newPosition);
      markerInstance.value.setPosition(newPosition);
    }

  } catch (error) {
    showNotification('Could not find the address on the map', 'error');
  }
};

// Reverse geocode coordinates to address
const reverseGeocode = async (lat, lng) => {
  if (!geocoderInstance.value) return;

  try {
    const response = await new Promise((resolve, reject) => {
      geocoderInstance.value.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error(`Reverse geocoding failed: ${status}`));
          }
        }
      );
    });

    // Update address field with the formatted address
    kurirToko.alamat = response.formatted_address;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
  }
};

// Watch for address changes to trigger geocoding
let geocodingTimeout = null;
watch(() => kurirToko.alamat, (newAddress) => {
  if (!newAddress || !kurirToko.mapLoaded) return;
  
  // Debounce geocoding requests
  clearTimeout(geocodingTimeout);
  geocodingTimeout = setTimeout(() => {
    geocodeAddress(newAddress);
  }, 1000);
});

// Initialize map when component is mounted and Kurir Toko is enabled
watch(() => shippingMethods.kurirToko, (enabled) => {
  if (enabled && !kurirToko.mapLoaded) {
    // Wait for DOM update and then initialize map
    setTimeout(async () => {
      
      // Try direct access to Google Maps first
      if (window.google && window.google.maps && window.google.maps.Map) {
        await initializeMap();
      } else {
        try {
          await loadGoogleMaps();
          await initializeMap();
        } catch (error) {
          showNotification('Failed to load map. Please refresh the page and try again.', 'error');
        }
      }
    }, 200);
  }
});

// Also watch for mapRef availability
watch(mapRef, (newMapRef) => {
  if (newMapRef && shippingMethods.kurirToko && !kurirToko.mapLoaded) {
    setTimeout(async () => {
      
      // Ensure Google Maps is loaded
      if (window.google && window.google.maps && window.google.maps.Map) {
        await initializeMap();
      } else {
        try {
          await loadGoogleMaps();
          await initializeMap();
        } catch (error) {
          console.error('Failed to load Google Maps via mapRef watcher:', error);
        }
      }
    }, 100);
  }
});

// Payment Methods
const paymentMethods = reactive({
  cod: false,
  bankTransfer: false,
  paymentGateway: false,
  qris: false
});

// Bank Transfer Accounts
const bankAccounts = ref([]);
const isSaving = ref(false);

function addBankAccount() {
  bankAccounts.value.push({
    id: Date.now(), // temporary ID
    bankName: '',
    accountNumber: '',
    accountHolder: ''
  });
}

function deleteBankAccount(index) {
  bankAccounts.value.splice(index, 1);
}

// QRIS Configuration
const qrisConfig = reactive({
  enabled: false, // Track if QRIS is enabled in UI (toggle state)
  imageUrl: '',
  blobKey: '',
  blobId: '',
  description: '',
});

// QRIS Image Handler
async function handleQrisImageUpload(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    // Validate file is an image
    if (!file.type.startsWith('image/')) {
      useAlert(t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.ALERTS.UPLOAD_INVALID_FILE'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      useAlert(t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.ALERTS.UPLOAD_SIZE_LIMIT'));
      return;
    }


    const response = await uploadAPI.uploadAttachment(file);

    qrisConfig.imageUrl = response.data.file_url;
    qrisConfig.blobKey = response.data.blob_key;
    qrisConfig.blobId = response.data.blob_id;
  } catch (error) {
    useAlert(t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.ALERTS.UPLOAD_FAILED'));
    console.error('QRIS image upload error:', error);
  }
}


function deleteQrisImage() {
  showDeleteQrisModal.value = true;
}

async function confirmDeleteQrisImage() {
  try {
    if (qrisConfig.blobKey || qrisConfig.blobId) {
      uploadAPI.deleteAttachment({ blobKey: qrisConfig.blobKey, blobId: qrisConfig.blobId }).catch(() => {
        console.warn('Failed to delete QRIS image from storage, but proceeding with configuration removal.');
      });
    }

    // Remove QRIS from flow_data and display_flow_data
    let flowData = JSON.parse(JSON.stringify(props.data.flow_data));
    let displayFlowData = JSON.parse(JSON.stringify(props.data.display_flow_data));
    const agentIndex = flowData.enabled_agents.indexOf('sales');
    
    if (agentIndex !== -1) {
      if (flowData.agents_config[agentIndex].configurations?.payment_options?.methods) {
        flowData.agents_config[agentIndex].configurations.payment_options.methods.forEach(method => {
          if (method.type === 'non_cod' && method.qris) {
            delete method.qris;
          }
        });
      }
      
      if (displayFlowData.agents_config[agentIndex].configurations?.payment_options?.methods) {
        displayFlowData.agents_config[agentIndex].configurations.payment_options.methods.forEach(method => {
          if (method.type === 'non_cod' && method.qris) {
            delete method.qris;
          }
        });
      }

      const payload = {
        flow_data: flowData,
        display_flow_data: displayFlowData,
      };

      await aiAgents.updateAgent(props.data.id, payload);
      emit('update:data');
    }

    // Reset QRIS configuration
    qrisConfig.enabled = false;
    qrisConfig.imageUrl = '';
    qrisConfig.blobKey = '';
    qrisConfig.blobId = '';
    qrisConfig.description = '';
    paymentMethods.qris = false;
    
    const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
    
    showDeleteQrisModal.value = false;
    useAlert(t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.ALERTS.DELETE_SUCCESS'));
  } catch (error) {
    console.error('Failed to delete QRIS:', error);
    showDeleteQrisModal.value = false;
    useAlert(t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.ALERTS.DELETE_ERROR'));
  }
}

// Payment Gateway
const paymentGateway = reactive({
  provider: 'duitku',
  apiKey: '',
  merchantCode: ''
});

const paymentGatewayProviders = [
  { label: 'Duitku', id: 'duitku' }
];

async function submitShippingConfig(updatedStores) {
  if (isSaving.value) return;

  try {
    isSaving.value = true;
    
    const storeResponse = await shippingStoresAPI.batchUpdate(props.data.id, updatedStores);
    const savedStoresWithIds = storeResponse.data;

    let flowData = JSON.parse(JSON.stringify(props.data.flow_data));
    let displayFlowData = JSON.parse(JSON.stringify(props.data.display_flow_data));

    const agentIndex = flowData.enabled_agents.indexOf('sales');
    
    if (agentIndex === -1) {
      useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.AGENT_NOT_FOUND'));
      return;
    }

    if (!flowData.agents_config[agentIndex].configurations) {
      flowData.agents_config[agentIndex].configurations = {};
    }
    if (!displayFlowData.agents_config[agentIndex].configurations) {
      displayFlowData.agents_config[agentIndex].configurations = {};
    }

    flowData.agents_config[agentIndex].configurations.shipping_options = {
      ...flowData.agents_config[agentIndex].configurations.shipping_options,
      stores: savedStoresWithIds 
    };

    displayFlowData.agents_config[agentIndex].configurations.shipping_options = {
      ...displayFlowData.agents_config[agentIndex].configurations.shipping_options,
      stores: savedStoresWithIds 
    };

    const agentPayload = {
      flow_data: flowData,
      display_flow_data: displayFlowData, 
    };

    await aiAgents.updateAgent(props.data.id, agentPayload);
    await store.dispatch('shippingStores/fetch', props.data.id);
    
    emit('update:data');
    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_SUCCESS')); 

  } catch (error) {
    console.error('Error saving shipping config:', error);
    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

async function submitPaymentConfig() {
  if (isSaving.value) return;

  try {
    isSaving.value = true;

    // Validate QRIS if enabled (toggle is on)
    if (paymentMethods.qris && !qrisConfig.imageUrl) {
      useAlert(t('AGENT_MGMT.SALESBOT.PAYMENT.QRIS.ALERTS.IMAGE_REQUIRED'));
      return;
    }

    // Generate payment configuration
    const paymentConfig = {
      methods: []
    };

    if (paymentMethods.cod) {
      paymentConfig.methods.push({
        type: "cod",
        name: "Bayar di Tempat (COD)"
      });
    }

    if (paymentMethods.bankTransfer || paymentMethods.paymentGateway || paymentMethods.qris) {
      const nonCodMethod = {
        type: "non_cod",
        name: "Transfer Online"
      };

      // Add bank transfer if enabled
      if (paymentMethods.bankTransfer) {
        nonCodMethod.bank_transfer = {
          accounts: bankAccounts.value.filter(account => 
            account.bankName && account.accountNumber && account.accountHolder
          )
        };
      }

      // Add payment gateway if enabled
      if (paymentMethods.paymentGateway) {
        nonCodMethod.payment_gateway = {
          provider: paymentGateway.provider,
          apiKey: paymentGateway.apiKey,
          merchantCode: paymentGateway.merchantCode
        };
      }


      if (qrisConfig.imageUrl || qrisConfig.blobKey || qrisConfig.blobId) {
        nonCodMethod.qris = {
          enabled: paymentMethods.qris, // Save the toggle state
          imageUrl: qrisConfig.imageUrl,
          blobKey: qrisConfig.blobKey,
          blobId: qrisConfig.blobId,
          description: qrisConfig.description,
        };
      }

      paymentConfig.methods.push(nonCodMethod);
    }

    // Save to backend
    let flowData = JSON.parse(JSON.stringify(props.data.flow_data));
    let displayFlowData = JSON.parse(JSON.stringify(props.data.display_flow_data));
    const agentIndex = flowData.enabled_agents.indexOf('sales');
    
    if (agentIndex === -1) {
      useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.AGENT_NOT_FOUND'))
      return;
    }

    // Initialize configurations if not exists
    if (!flowData.agents_config[agentIndex].configurations) {
      flowData.agents_config[agentIndex].configurations = {};
    }
    
    // Update payment options configuration
    flowData.agents_config[agentIndex].configurations.payment_options = paymentConfig;
    displayFlowData.agents_config[agentIndex].configurations.payment_options = paymentConfig;

    const payload = {
      flow_data: flowData,
      display_flow_data: displayFlowData, 
    };

    await aiAgents.updateAgent(props.data.id, payload);
    emit('update:data');

    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

// Submit Cart Configuration
async function submitCartConfig() {
  if (isSaving.value) return;

  try {
    isSaving.value = true;

    // Save to backend
    let flowData = JSON.parse(JSON.stringify(props.data.flow_data)); 
    let displayFlowData = JSON.parse(JSON.stringify(props.data.display_flow_data));

    const agentIndex = flowData.enabled_agents.indexOf('sales');
    
    if (agentIndex === -1) {
      useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.AGENT_NOT_FOUND'))
      return;
    }

    // Initialize configurations if not exists
    if (!flowData.agents_config[agentIndex].configurations) {
      flowData.agents_config[agentIndex].configurations = {};
    }
    
    // Update cart configuration
    flowData.agents_config[agentIndex].configurations.cart_enabled = cartEnabled.value;
    displayFlowData.agents_config[agentIndex].configurations.cart_enabled = cartEnabled.value;

    const payload = {
      flow_data: flowData,
      display_flow_data: displayFlowData, 
    };

    await aiAgents.updateAgent(props.data.id, payload);
    emit('update:data');

    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

async function saveSettings() {
  if (isSaving.value) return;

  try {
    isSaving.value = true;
    let flowData = JSON.parse(JSON.stringify(props.data.flow_data));
    let displayFlowData = JSON.parse(JSON.stringify(props.data.display_flow_data));

    const agentIndex = flowData.enabled_agents.indexOf('sales');

    if (agentIndex === -1) {
      useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.AGENT_NOT_FOUND'));
      return;
    }

    // Pastikan object configurations ada
    if (!flowData.agents_config[agentIndex].configurations) {
      flowData.agents_config[agentIndex].configurations = {};
    }

    // Simpan Tingkat Kreativitas
    flowData.agents_config[agentIndex].temperature = creativityLevel.value;
    displayFlowData.agents_config[agentIndex].temperature = creativityLevel.value;

    flowData.agents_config[agentIndex].configurations.reminder_offer_enabled = reminderOfferEnabled.value;
    displayFlowData.agents_config[agentIndex].configurations.reminder_offer_enabled = reminderOfferEnabled.value;

    flowData.debounce_config = { enabled: isDelayEnabled.value, interval_seconds: debounceInterval.value };
    displayFlowData.debounce_config = { enabled: isDelayEnabled.value, interval_seconds: debounceInterval.value };

    const payload = {
      flow_data: flowData,
      display_flow_data: displayFlowData,
    };

    await Promise.all([
      aiAgents.updateAgent(props.data.id, payload),
      idleConfigsAPI.updateConfig(props.data.id, {
        enabled: idleConfig.enabled,
        duration: idleConfig.duration,
      })
    ]);
    emit('update:data');

    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    console.error('Save settings error:', error);
    useAlert(t('AGENT_MGMT.WEBSITE_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

// Function to load saved configuration from backend
function loadSavedConfiguration() {
  try {
    const flowData = props.data.display_flow_data;
    if (!flowData) {
      return;
    }
    
    const agentIndex = flowData.enabled_agents.indexOf('sales');
    
    if (agentIndex === -1) {
      return;
    }
    
    const agentData = flowData.agents_config[agentIndex];
    const config = agentData?.configurations;
    
    if (!config) {
      return;
    }

    // Load Creativity Level
    if (agentData.temperature !== undefined) {
      creativityLevel.value = agentData.temperature;
    }

    const salesConfig = agentData?.configurations;
    reminderOfferEnabled.value = salesConfig?.reminder_offer_enabled === true;
    isDelayEnabled.value = flowData?.debounce_config?.enabled === true;
    debounceInterval.value = flowData?.debounce_config?.interval_seconds || 10;

    // Reset all shipping methods first
    shippingMethods.kurirToko = false;
    shippingMethods.kurirBiasa = false;
    shippingMethods.ambilToko = false;
    
    // Reset all shipping configs
    Object.assign(kurirToko, {
      alamat: '',
      radius: '',
      wilayah: '',
      kotaWilayah: '', // Reset city selection
      serviceAreaType: 'radius', // Reset to default
      pricingMethod: 'flatRate', // Reset to default
      flatRate: '',
      biayaPerJarak: '',
      gratisOngkir: false,
      minimalBelanja: '',
      estimasi: '',
      latitude: -6.2088, // Default to Jakarta
      longitude: 106.8456,
      mapLoaded: false
    });
    
    Object.assign(kurirBiasa, {
      provinsi: '',
      kota: '',
      kecamatan: '',
      kelurahan: '',
      jalan: '',
      kodePos: '',
      kurir: []
    });
    
    Object.assign(ambilToko, {
      alamat: '',
      jamBuka: '',
      jamTutup: '',
      estimasi: ''
    });

    // Load Shipping Configuration
    if (config.shipping_options && config.shipping_options.methods) {
      
      config.shipping_options.methods.forEach(method => {
        
        if (method.type === 'store_courier') {
          shippingMethods.kurirToko = true;
          
          // Handle store_address as object or string
          if (method.store_address) {
            if (typeof method.store_address === 'object' && method.store_address.address) {
              // New format: object with address and coordinates
              kurirToko.alamat = method.store_address.address || '';
              if (method.store_address.coordinates) {
                kurirToko.latitude = method.store_address.coordinates.latitude || -6.2088; // Default to Jakarta
                kurirToko.longitude = method.store_address.coordinates.longitude || 106.8456;
              }
            } else if (typeof method.store_address === 'string') {
              // Old format: plain string
              kurirToko.alamat = method.store_address;
            }
          }
          
          // Parse service area (radius or region format)
          if (method.service_area) {
            // Check for radius format: "Radius 15 km" or "Radius 15km"
            const radiusMatch = method.service_area.match(/Radius\s*(\d+)\s*km/i);
            if (radiusMatch) {
              kurirToko.serviceAreaType = 'radius';
              kurirToko.radius = radiusMatch[1];
            }
            // Check for region format with readable names (new format)
            else if (method.service_area.includes('Sekitar')) {
              kurirToko.serviceAreaType = 'region';
              
              // Try to extract IDs from fallback format: "Kota-3273" or "Provinsi-32"
              const kotaIdMatch = method.service_area.match(/Kota-(\d+)/);
              if (kotaIdMatch) {
                kurirToko.kotaWilayah = kotaIdMatch[1];
              }
              
              const provinsiIdMatch = method.service_area.match(/Provinsi-(\d+)/);
              if (provinsiIdMatch) {
                kurirToko.wilayah = provinsiIdMatch[1];
              }
              
              // If no ID format found, try to match against known province/city names
              if (!kurirToko.wilayah || !kurirToko.kotaWilayah) {
                // Find province ID by matching name
                const provinsiEntry = Object.entries(provinsiJson).find(([id, name]) => {
                  return method.service_area.includes(name);
                });
                if (provinsiEntry) {
                  kurirToko.wilayah = provinsiEntry[0];
                  
                  // Load cities for this province and try to match city name
                  try {
                    import(`../wilayah/kabupaten_kota/kab-${provinsiEntry[0]}.json`).then(kabupatenModule => {
                      const kabupatenJson = kabupatenModule.default || kabupatenModule;
                      const kotaEntry = Object.entries(kabupatenJson).find(([id, name]) => {
                        return method.service_area.includes(name);
                      });
                      if (kotaEntry) {
                        kurirToko.kotaWilayah = kotaEntry[0];
                        // Reload city options for this province
                        loadServiceAreaKota(kurirToko.wilayah);
                      }
                    });
                  } catch (error) {
                    console.log('Could not load city data for matching');
                  }
                }
              }
            }
            // Legacy format support: "Provinsi 32" or "Sekitar Provinsi 32"
            else if (method.service_area.includes('Provinsi')) {
              kurirToko.serviceAreaType = 'region';
              
              // Extract province ID (handle both "Sekitar Provinsi 32" and "Provinsi 32")
              const provinsiMatch = method.service_area.match(/(?:Sekitar\s+)?Provinsi\s*(\d+)/i);
              if (provinsiMatch) {
                kurirToko.wilayah = provinsiMatch[1];
                // Load city options for this province
                loadServiceAreaKota(kurirToko.wilayah);
              }
              
              // Extract city ID if present
              const kotaMatch = method.service_area.match(/Kota\s*(\d+)/i);
              if (kotaMatch) {
                kurirToko.kotaWilayah = kotaMatch[1];
              }
            }
          }
          
          // Parse delivery cost info
          if (method.delivery_cost_info) {
            
            if (method.delivery_cost_info.includes('Flat rate')) {
              kurirToko.pricingMethod = 'flatRate';
              const flatRateMatch = method.delivery_cost_info.match(/Rp\s*([\d,]+)/);
              if (flatRateMatch) {
                kurirToko.flatRate = flatRateMatch[1].replace(/,/g, '');
              }
            } else if (method.delivery_cost_info.includes('/km')) {
              kurirToko.pricingMethod = 'perDistance';
              const perKmMatch = method.delivery_cost_info.match(/Rp\s*([\d,]+)\/km/);
              if (perKmMatch) {
                kurirToko.biayaPerJarak = perKmMatch[1].replace(/,/g, '');
              }
            }
            
            kurirToko.gratisOngkir = method.delivery_cost_info.includes('Gratis ongkir');
            
            const minimalMatch = method.delivery_cost_info.match(/minimal belanja Rp\s*([\d,]+)/i);
            if (minimalMatch) {
              kurirToko.minimalBelanja = minimalMatch[1].replace(/,/g, '');
            }
          }
          
          // Load estimated delivery time
          if (method.estimated_delivery_time) {
            kurirToko.estimasi = method.estimated_delivery_time;
          }
        }
        
        if (method.type === 'regular_courier') {
          shippingMethods.kurirBiasa = true;
          
          // Parse store_address string back to address components
          if (method.store_address) {
            // Try to extract jalan from the full address
            const addressParts = method.store_address.split(',');
            if (addressParts.length > 0) {
              kurirBiasa.jalan = addressParts[0].trim();
            }
            
            // Extract postal code if present
            const postalMatch = method.store_address.match(/(\d{5})$/);
            if (postalMatch) {
              kurirBiasa.kodePos = postalMatch[1];
            }
          }
          
          // Load available couriers
          if (method.available_couriers && Array.isArray(method.available_couriers)) {
            kurirBiasa.kurir = method.available_couriers;
          }
        }
        
        if (method.type === 'store_pickup') {
          shippingMethods.ambilToko = true;
          ambilToko.alamat = method.store_address || '';
          
          // Parse operating hours (e.g., "08:00 - 17:00")
          if (method.operating_hours) {
            const hoursMatch = method.operating_hours.match(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
            if (hoursMatch) {
              ambilToko.jamBuka = hoursMatch[1];
              ambilToko.jamTutup = hoursMatch[2];
            }
          }
          
          if (method.pickup_ready_time) {
            ambilToko.estimasi = method.pickup_ready_time;
          }
        }
      });
    }

    // Reset all payment methods first
    paymentMethods.cod = false;
    paymentMethods.bankTransfer = false;
    paymentMethods.paymentGateway = false;
    paymentMethods.qris = false;
    
    // Reset payment configs
    bankAccounts.value = [];
    Object.assign(paymentGateway, {
      provider: 'duitku',
      apiKey: '',
      merchantCode: ''
    });
    Object.assign(qrisConfig, {
      enabled: false,
      imageUrl: '',
      blobKey: '',
      blobId: '',
      description: '',
    });

    // Load Payment Configuration
    if (config.payment_options && config.payment_options.methods) {
      
      config.payment_options.methods.forEach(method => {
        
        if (method.type === 'cod') {
          paymentMethods.cod = true;
        }
        
        if (method.type === 'non_cod') {
          
          // Check if bank transfer is available
          if (method.bank_transfer && method.bank_transfer.accounts) {
            paymentMethods.bankTransfer = true;
            
            bankAccounts.value = method.bank_transfer.accounts.map(acc => ({
              id: Date.now() + Math.random(),
              bankName: acc.bankName || '',
              accountNumber: acc.accountNumber || '',
              accountHolder: acc.accountHolder || ''
            }));
          }
          
          // Check if payment gateway is available
          if (method.payment_gateway) {
            paymentMethods.paymentGateway = true;
            
            paymentGateway.provider = method.payment_gateway.provider || 'duitku';
            paymentGateway.apiKey = method.payment_gateway.apiKey || '';
            paymentGateway.merchantCode = method.payment_gateway.merchantCode || '';
          }

          // Check if QRIS is available
          if (method.qris) {
            const isEnabled = method.qris.enabled !== undefined ? method.qris.enabled : true;
            paymentMethods.qris = isEnabled;
            qrisConfig.enabled = isEnabled;
            qrisConfig.imageUrl = method.qris.imageUrl || '';
            qrisConfig.blobKey = method.qris.blobKey || '';
            qrisConfig.blobId = method.qris.blobId || '';
            qrisConfig.description = method.qris.description || '';
          }
        }
      });
    }

    // Load Cart Configuration
    if (config.cart_enabled !== undefined) {
      cartEnabled.value = config.cart_enabled;
    }
    
  } catch (error) {
  }
}

// Function to update local props data after successful save
// Removed updateLocalPropsData function - it was mutating props directly (Vue anti-pattern)
// Props updates now handled cleanly via emit('update:data') and parent refresh
</script>

<style scoped>
/* Enhanced dropdown styling with proper scrolling */
.dropdown-container {
  position: relative;
}

.dropdown-input {
  transition: all 0.2s ease-in-out;
  position: relative;
  z-index: 2;
}

.dropdown-input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.dropdown-menu {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.15s ease-in-out;
  transform-origin: top;
  border: 1px solid rgba(209, 213, 219, 1);
}

.dropdown-item {
  transition: background-color 0.15s ease-in-out;
  border-bottom: 1px solid rgba(243, 244, 246, 1);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  transform: translateX(2px);
}

/* Custom scrollbar styling */
.scrollbar-thin {
  scrollbar-width: thin;
}

.scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
  background-color: rgb(209 213 219);
  border-radius: 9999px;
}

.scrollbar-track-gray-100::-webkit-scrollbar-track {
  background-color: rgb(243 244 246);
  border-radius: 9999px;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: rgb(243 244 246);
  border-radius: 9999px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgb(209 213 219);
  border-radius: 9999px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgb(156 163 175);
}

/* Dark mode scrollbar */
.dark .scrollbar-thumb-gray-600::-webkit-scrollbar-thumb {
  background-color: rgb(75 85 99);
}

.dark .scrollbar-track-gray-800::-webkit-scrollbar-track {
  background-color: rgb(31 41 55);
}

.dark .scrollbar-thin::-webkit-scrollbar-track {
  background: rgb(31 41 55);
}

.dark .scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgb(75 85 99);
}

.dark .scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgb(107 114 128);
}

/* Dark mode dropdown adjustments */
.dark .dropdown-menu {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2);
  border-color: rgb(75 85 99);
}

.dark .dropdown-item {
  border-bottom-color: rgb(55 65 81);
}

/* Ensure dropdown appears above other elements */
.dropdown-menu {
  z-index: 9999 !important;
}

/* Fix for dropdown positioning in containers with overflow */
.dropdown-container:has(.dropdown-menu[style*="display: block"]) {
  z-index: 9999;
}
.btn-retryauth {
    border: 2px solid #B0B1BC !important;
    color: #B0B1BC !important;
}
.btn-retryauth:hover {       
    background-color: rgba(176, 177, 188, 0.1) !important; 
}
.dark .btn-retryauth:hover {
    background-color: rgba(176, 177, 188, 0.1) !important; 
}

</style>